import { PlayingRangeBadge } from '../components/PlayingLevel'
import { useMemberDashboardGreeting } from '../lib/useMemberDashboardGreeting'
import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { formatSportTypeLabel } from '../api/racquetCourts'
import { formatFacilityBookingStatus } from '../utils/facilityBookingPresentation'
import { useMembersConversationOverlay, useMembersBookingOverlay } from '../lib/useMembersConversationOverlay'
import MobileMemberDashboard from '../components/MobileMemberDashboard'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GraduationCap, Headset, CalendarClock, CheckCircle2, ChevronRight, Clock3, CreditCard, Dumbbell, Flag, Goal, Globe, Home, MapPin, ReceiptText, ShieldCheck, Sparkles, Ticket, Utensils, UsersRound, FileText, X } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import MemberFrontDeskModal from '../components/MemberFrontDeskModal'
import MemberDashboardModal from '../components/MemberDashboardModal'
import { cancellationUnavailableMessage } from '../lib/cancellationAvailability'
import { normalizeFocus } from '@/lib/imageFocus'
import { formatDashboardCurrency } from '../lib/formatCurrency'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { memberDisplayCacheKey, readMemberDisplayCache } from '../lib/memberDisplayCache'
import { readMemberResource, writeMemberResource } from '../lib/resourceCache'
import { isExactMemberRequestCurrent } from '../lib/memberRequestIdentity'
import { startBootstrapSpan } from '../lib/bootstrapTelemetry'
import { bookingDomain, bookingIdentityKey, canonicalBookingId } from '../lib/bookingIdentity'
import MembersLoading from '../components/MembersLoading'

const MEMBER_PORTAL_TOAST_DURATION_MS = 5000

export function BookingThumbnail({ booking, src }) {
  const { t: memberText } = useOptionalPrivanaI18n()
  const [failedSrc, setFailedSrc] = useState(null)
  const focusX = normalizeFocus(booking?.imageFocusX ?? booking?.image_focus_x)
  const focusY = normalizeFocus(booking?.imageFocusY ?? booking?.image_focus_y)
  const module = String(booking?.source || booking?.type || '').toLowerCase()
  const FallbackIcon = module.includes('golf') ? Flag : module.includes('dining') ? Utensils : module.includes('spa') ? Sparkles : module.includes('fitness') ? Dumbbell : module.includes('room') || module.includes('hotel') ? Home : Ticket
  const safeSrc = typeof src === 'string' && /^(https:\/\/|data:image\/|blob:)/i.test(src.trim()) ? src.trim() : null
  return safeSrc && failedSrc !== safeSrc
    ? <img className="mp-upcoming-booking-row__image" src={safeSrc} alt="" loading="lazy" onError={() => setFailedSrc(safeSrc)} style={{ objectPosition: `${focusX}% ${focusY}%` }} />
    : <span className={`mp-upcoming-booking-row__image mp-booking-thumbnail__fallback mp-booking-thumbnail__fallback--${module || 'generic'}`} aria-label={memberText("portalPresentation.member_booking_image_unavailable")} role="img"><FallbackIcon size={22} /></span>
}

async function readMemberPortalPayload(res) {
  if (res && typeof res.json === 'function') {
    return res.json()
  }
  return res?.data || res?.res || res || {}
}

function MemberPortalToast({ message, variant = 'success', resetKey, onDismiss }) {
  const { t: memberText } = useOptionalPrivanaI18n()

  const timeoutRef = useRef(null)

  const clearToastTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const dismissToast = useCallback(() => {
    clearToastTimeout()
    onDismiss?.()
  }, [clearToastTimeout, onDismiss])

  useEffect(() => {
    if (!message) return undefined
    clearToastTimeout()
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      onDismiss?.()
    }, MEMBER_PORTAL_TOAST_DURATION_MS)
    return clearToastTimeout
  }, [clearToastTimeout, message, onDismiss, resetKey])

  if (!message) return null

  return (
    <div className={`mp-toast mp-toast--${variant}`} role={variant === 'error' ? 'alert' : 'status'}>
      <span>{message}</span>
      <button type="button" className="mp-toast__close" onClick={dismissToast} aria-label={memberText("portalPresentation.member_dismiss_notification")}>
        <X size={14} strokeWidth={2.5} aria-hidden="true" />
      </button>
    </div>
  )
}

export default function MemberDashboardPage({
  member,
  heroImageUrl,
  heroImageFocus,
  clubLogoUrl,
  clubCode,
  upcomingBookings,
  upcomingLoading,
  upcomingError,
  summary,
  onUpcomingBookingsRefresh,
  displayCacheScope,
  isMobileApp = false,
}) {
  const clubTimeZone = useMemberTimeZone()
  const greeting = useMemberDashboardGreeting(clubTimeZone)
  const { t: memberText } = useOptionalPrivanaI18n()

  const [compactViewport, setCompactViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth < 960)
  useEffect(() => {
    const update = () => setCompactViewport(window.innerWidth < 960)
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  const isCompactDashboard = isMobileApp || compactViewport

  const heroFocus = useMemo(
    () => ({
      x: normalizeFocus(heroImageFocus?.x ?? heroImageFocus?.hero_image_focus_x),
      y: normalizeFocus(heroImageFocus?.y ?? heroImageFocus?.hero_image_focus_y),
    }),
    [heroImageFocus]
  )
  const { t: academyTranslate, locale } = useOptionalPrivanaI18n();
  const navigate = useNavigate()
  const location = useLocation()
  const openedNavigationBookingRef = useRef(null)
  const navigationBookingRefreshRef = useRef(null)
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const [isAllReservationsOpen, setIsAllReservationsOpen] = useMembersBookingOverlay()
  const [upcomingCarouselIndex, setUpcomingCarouselIndex] = useState(0)
  const upcomingCarouselRef = useRef(null)
  const upcomingCarouselBookingIdRef = useRef(null)
  const upcomingDetailRequestRef = useRef(0)
  const selectedUpcomingIdentityRef = useRef(null)
  const upcomingDetailCacheRef = useRef(new Map())
  const upcomingDetailInflightRef = useRef(new Map())
  const upcomingDetailSessionRef = useRef(0)
  const [clubNews, setClubNews] = useState(() => readMemberResource(displayCacheScope, 'club-news', { ttlMs: 15 * 60 * 1000 })?.data || readMemberDisplayCache(displayCacheScope)?.data?.clubNews || [])
  const [clubNewsLoading, setClubNewsLoading] = useState(false)
  const [clubNewsError, setClubNewsError] = useState('')
  const [clubNewsConfirmed, setClubNewsConfirmed] = useState(false)
  const [clubNewsRetry, setClubNewsRetry] = useState(0)
  const displayCacheScopeKey = memberDisplayCacheKey(displayCacheScope)
  const activeDisplayScopeRef = useRef(null)
  activeDisplayScopeRef.current = {
    key: displayCacheScopeKey,
    clubCode: displayCacheScope?.clubCode || null,
    subject: displayCacheScope?.memberIdentity || null,
  }
  const [selectedClubNews, setSelectedClubNews] = useState(null)
  const [isAllClubNewsOpen, setIsAllClubNewsOpen] = useState(false)
  const [entryNotifications, setEntryNotifications] = useState([])
  const [entryNotificationReadLoading, setEntryNotificationReadLoading] = useState(false)
  const [entryNotificationPdfUrl, setEntryNotificationPdfUrl] = useState('')
  const [clubNewsPdfUrl, setClubNewsPdfUrl] = useState('')
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [selectedUpcomingBooking, setSelectedUpcomingBooking] = useState(null)
  const [selectedGolfBooking, setSelectedGolfBooking] = useState(null)
  const [golfBookingLoading, setGolfBookingLoading] = useState(false)
  const [golfCancelConfirmOpen, setGolfCancelConfirmOpen] = useState(false)
  const [golfCancelError, setGolfCancelError] = useState('')
  const [golfCancelSuccess, setGolfCancelSuccess] = useState('')
  const [golfCancelSuccessKey, setGolfCancelSuccessKey] = useState(0)
  const [golfCancelErrorToast, setGolfCancelErrorToast] = useState('')
  const [golfCancelErrorToastKey, setGolfCancelErrorToastKey] = useState(0)
  const [racquetCancelConfirmOpen, setRacquetCancelConfirmOpen] = useState(false)
  const [racquetCancelError, setRacquetCancelError] = useState('')
  const [racquetCancelSuccess, setRacquetCancelSuccess] = useState('')
  const [racquetCancelSuccessKey, setRacquetCancelSuccessKey] = useState(0)
  const [racquetCancelErrorToast, setRacquetCancelErrorToast] = useState('')
  const [racquetCancelErrorToastKey, setRacquetCancelErrorToastKey] = useState(0)
  const [spaCancelConfirmOpen, setSpaCancelConfirmOpen] = useState(false)
  const [spaCancelError, setSpaCancelError] = useState('')
  const [spaCancelSuccess, setSpaCancelSuccess] = useState('')
  const [spaCancelSuccessKey, setSpaCancelSuccessKey] = useState(0)
  const [spaCancelErrorToast, setSpaCancelErrorToast] = useState('')
  const [spaCancelErrorToastKey, setSpaCancelErrorToastKey] = useState(0)
  const [fitnessCancelConfirmOpen, setFitnessCancelConfirmOpen] = useState(false)
  const [fitnessCancelError, setFitnessCancelError] = useState('')
  const [fitnessCancelSuccess, setFitnessCancelSuccess] = useState('')
  const [fitnessCancelSuccessKey, setFitnessCancelSuccessKey] = useState(0)
  const [fitnessCancelErrorToast, setFitnessCancelErrorToast] = useState('')
  const [fitnessCancelErrorToastKey, setFitnessCancelErrorToastKey] = useState(0)
  const [hotelCancelConfirmOpen, setHotelCancelConfirmOpen] = useState(false)
  const [hotelCancelError, setHotelCancelError] = useState('')
  const [hotelCancelSuccess, setHotelCancelSuccess] = useState('')
  const [hotelCancelSuccessKey, setHotelCancelSuccessKey] = useState(0)
  const [hotelCancelErrorToast, setHotelCancelErrorToast] = useState('')
  const [hotelCancelErrorToastKey, setHotelCancelErrorToastKey] = useState(0)
  const [selectedBookingLoading, setSelectedBookingLoading] = useState(false)
  const [upcomingDetailError, setUpcomingDetailError] = useState('')
  const viewAllButtonRef = useRef(null)
  const [isContactFrontDeskOpen, setIsContactFrontDeskOpen] = useMembersConversationOverlay()

  const isMemberResolving = Boolean(member?.isResolving)
  const memberName = member?.firstName || member?.email || memberText("portalPresentation.member_member")
  const [cancelledUpcomingBookingIds, setCancelledUpcomingBookingIds] = useState(() => new Set())
  const safeBookings = useMemo(() => {
    const bookings = Array.isArray(upcomingBookings)
      ? upcomingBookings.filter((booking) => booking && typeof booking === 'object' && !Array.isArray(booking))
      : []
    if (!cancelledUpcomingBookingIds.size) return bookings
    return bookings.filter((booking) => !cancelledUpcomingBookingIds.has(bookingIdentityKey(booking, { club: clubCode, member: member?.id || member?.member_id || displayCacheScopeKey })))
  }, [cancelledUpcomingBookingIds, upcomingBookings, clubCode, member?.id, member?.member_id, displayCacheScopeKey])
  const outstandingBalance = summary?.balance ?? summary?.outstandingBalance

  useEffect(() => {
    console.info('[member-portal] dashboard safeBookings length', { length: safeBookings.length })
  }, [safeBookings.length])

  const currencyCode = summary?.currency?.code || 'USD'
  const currencySymbol = summary?.currency?.symbol || '$'

  const stats = useMemo(
    () => [
      { label: memberText("portalPresentation.member_upcoming_bookings"), value: safeBookings.length },
      {
        label: memberText("portalPresentation.member_current_balance"),
        value: outstandingBalance,
        displayValue: outstandingBalance == null ? (summary?.accountError ? memberText("membersHome.balanceError") : memberText("portalPresentation.member_balance_loading")) : formatDashboardCurrency(outstandingBalance, { locale, code: currencyCode, symbol: currencySymbol }),
      },
    ],
    [outstandingBalance, safeBookings.length, currencyCode, currencySymbol, summary?.accountError, memberText, locale]
  )

  const previewBookings = safeBookings.slice(0, 6)
  const previewClubNews = clubNews.slice(0, 3)
  const activeEntryNotification = entryNotifications[0] || null

  useEffect(() => {
    if (!displayCacheScopeKey || displayCacheScope?.clubCode !== clubCode || !displayCacheScope?.memberIdentity) {
      setClubNews([])
      return
    }
    const cached = readMemberResource(displayCacheScope, 'club-news', { ttlMs: 15 * 60 * 1000 })?.data || readMemberDisplayCache(displayCacheScope)?.data?.clubNews
    setClubNews(Array.isArray(cached) ? cached : [])
  }, [clubCode, displayCacheScope, displayCacheScopeKey])

  useEffect(() => {
    if (!clubCode || !displayCacheScopeKey || displayCacheScope?.clubCode !== clubCode || !displayCacheScope?.memberIdentity || member?.portal_access_enabled === false) return undefined
    let cancelled = false
    const controller = new AbortController()
    const request = {
      key: displayCacheScopeKey,
      clubCode,
      subject: displayCacheScope.memberIdentity,
      cacheScope: displayCacheScope,
    }
    const isCurrent = () => {
      const current = activeDisplayScopeRef.current
      return isExactMemberRequestCurrent(
        { identityKey: current?.key, clubCode: current?.clubCode, subject: current?.subject },
        { identityKey: request.key, clubCode: request.clubCode, subject: request.subject },
        cancelled
      )
    }
    setClubNewsLoading(true)
    setClubNewsError('')
    const endRequest = startBootstrapSpan('club_news_request', { clubCode, category: 'news' })
    memberPortalApiGet(`/api/members-portal/${encodeURIComponent(clubCode)}/club-news`, { signal: controller.signal })
      .then(readMemberPortalPayload)
      .then((payload) => {
        if (!isCurrent()) return
        const news = Array.isArray(payload.news) ? payload.news : []
        setClubNews(news)
        setClubNewsConfirmed(true)
        if (isCurrent()) writeMemberResource(request.cacheScope, 'club-news', news)
        endRequest({ status: 'success' })
      })
      .catch((error) => { if (isCurrent() && error?.name !== 'AbortError') setClubNewsError("membersHome.newsError"); endRequest({ status: 'failed', reason: 'request_failed' }) /* retain stale news */ })
      .finally(() => { if (isCurrent()) setClubNewsLoading(false) })
    return () => { cancelled = true; controller.abort() }
  }, [clubCode, clubNewsRetry, displayCacheScope, displayCacheScopeKey, member?.portal_access_enabled, memberPortalApiGet])


  useEffect(() => {
    if (!clubCode || member?.portal_access_enabled === false) return undefined
    let cancelled = false
    memberPortalApiGet(`/api/members-portal/${encodeURIComponent(clubCode)}/entry-notifications/unread`)
      .then(readMemberPortalPayload)
      .then((payload) => { if (!cancelled) setEntryNotifications(Array.isArray(payload.notifications) ? payload.notifications : []) })
      .catch(() => { if (!cancelled) setEntryNotifications([]) })
    return () => { cancelled = true }
  }, [clubCode, member?.portal_access_enabled, memberPortalApiGet])

  useEffect(() => {
    if (!activeEntryNotification || activeEntryNotification.announcement_type !== 'upload') { setEntryNotificationPdfUrl(''); return undefined }
    let cancelled = false
    memberPortalApiGet(`/api/members-portal/${encodeURIComponent(clubCode)}/club-news/${encodeURIComponent(activeEntryNotification.id)}/file-url`)
      .then(readMemberPortalPayload)
      .then((payload) => { if (!cancelled) setEntryNotificationPdfUrl(payload.url || '') })
      .catch(() => { if (!cancelled) setEntryNotificationPdfUrl('') })
    return () => { cancelled = true }
  }, [activeEntryNotification, clubCode, memberPortalApiGet])

  const openClubNews = useCallback((item) => {
    setIsAllClubNewsOpen(false)
    setSelectedClubNews(item)
    if (item?.id) memberPortalApiPost(`/api/members-portal/${encodeURIComponent(clubCode)}/club-news/${encodeURIComponent(item.id)}/view`, {}).catch(() => {})
  }, [clubCode, memberPortalApiPost])

  const markEntryNotificationRead = useCallback(() => {
    if (!activeEntryNotification?.id || entryNotificationReadLoading) return
    setEntryNotificationReadLoading(true)
    memberPortalApiPost(`/api/members-portal/${encodeURIComponent(clubCode)}/entry-notifications/${encodeURIComponent(activeEntryNotification.id)}/read`, {})
      .finally(() => {
        setEntryNotifications((items) => items.filter((item) => String(item.id) !== String(activeEntryNotification.id)))
        setEntryNotificationReadLoading(false)
      })
  }, [activeEntryNotification, clubCode, entryNotificationReadLoading, memberPortalApiPost])

  useEffect(() => {
    if (!selectedClubNews || selectedClubNews.announcement_type !== 'upload') { setClubNewsPdfUrl(''); return undefined }
    let cancelled = false
    memberPortalApiGet(`/api/members-portal/${encodeURIComponent(clubCode)}/club-news/${encodeURIComponent(selectedClubNews.id)}/file-url`)
      .then(readMemberPortalPayload)
      .then((payload) => { if (!cancelled) setClubNewsPdfUrl(payload.url || '') })
      .catch(() => { if (!cancelled) setClubNewsPdfUrl('') })
    return () => { cancelled = true }
  }, [clubCode, memberPortalApiGet, selectedClubNews])

  useEffect(() => {
    if (!golfCancelSuccess) return undefined
    const timeoutId = window.setTimeout(() => {
      setGolfCancelSuccess('')
    }, 5000)
    return () => window.clearTimeout(timeoutId)
  }, [golfCancelSuccess])

  const closeAllReservationsModal = useCallback(() => {
    upcomingDetailRequestRef.current += 1
    upcomingDetailSessionRef.current += 1
    upcomingDetailCacheRef.current.clear()
    upcomingDetailInflightRef.current.clear()
    setIsAllReservationsOpen(false)
    selectedUpcomingIdentityRef.current = null
    setSelectedUpcomingBooking(null)
    setSelectedBooking(null)
    setSelectedGolfBooking(null)
    setUpcomingDetailError('')
    setSelectedBookingLoading(false)
    setGolfCancelConfirmOpen(false)
    setRacquetCancelConfirmOpen(false)
    setSpaCancelConfirmOpen(false)
    setFitnessCancelConfirmOpen(false)
    setHotelCancelConfirmOpen(false)
    setGolfCancelSuccess('')
  }, [setIsAllReservationsOpen])

  const openAllReservationsModal = () => {
    if (isAllReservationsOpen) return
    upcomingDetailSessionRef.current += 1
    upcomingDetailCacheRef.current.clear()
    upcomingDetailInflightRef.current.clear()
    setIsAllReservationsOpen(true)
  }

  const formatDateOnly = (value) => {
    if (!value) return memberText('facilityBooking.tbd')
    const date = new Date(value)
    if (!Number.isFinite(date.getTime())) return memberText('facilityBooking.tbd')
    const timeZone = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? 'UTC' : clubTimeZone
    return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long', ...(timeZone ? { timeZone } : {}) }).replace(',', '')
  }
  const formatTimeOnly = (value) => {
    if (!value || /^\d{4}-\d{2}-\d{2}$/.test(String(value))) return memberText('facilityBooking.tbd')
    const date = new Date(value)
    if (!Number.isFinite(date.getTime())) return memberText('facilityBooking.tbd')
    return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true, ...(clubTimeZone ? { timeZone: clubTimeZone } : {}) })
  }
  const formatDateTime = (value) => {
    if (!value) return memberText('facilityBooking.tbd')
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return formatDateOnly(value)
    const date = new Date(value)
    if (!Number.isFinite(date.getTime())) return memberText('facilityBooking.tbd')
    return `${date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric', ...(clubTimeZone ? { timeZone: clubTimeZone } : {}) })} • ${formatTimeOnly(value)}`
  }

  useEffect(() => { const refresh = () => onUpcomingBookingsRefresh?.(); window.addEventListener('academy:committed', refresh); return () => window.removeEventListener('academy:committed', refresh); }, [onUpcomingBookingsRefresh]);

  const quickActions = [
    { label: memberText("portalPresentation.member_book_facilities"), mobileLabel: memberText("academyP2.book"), icon: Home, action: () => navigate(`/${clubCode}/book-facilities`) },
    { label: memberText("portalPresentation.member_explore_events"), mobileLabel: memberText("membersHome.events"), icon: CalendarClock, action: () => navigate(`/${clubCode}/events`) },
    {
      label: memberText("portalPresentation.member_contact_front_desk"),
      mobileLabel: memberText("memberFrontDesk.desk"),
      icon: Headset,
      action: () => setIsContactFrontDeskOpen(true),
    },
    { label: memberText("membersHome.account"), mobileLabel: memberText("membersHome.account"), icon: CreditCard, action: () => navigate(`/${clubCode}/account`) },
  ]

  const getUpcomingBookingId = canonicalBookingId

  const formatBookingType = (booking) => {
    if (isGolfUpcomingBooking(booking)) return memberText("portalPresentation.member_golf_booking_caption")
    if (booking?.openMatch && booking?.source === 'racquet') {
      // This tag is constructed from the sport enum by membersPortalUpcoming.mjs.
      const sport = String(booking.tag || '').split(' · ')[0].toLowerCase()
      if (['tennis', 'padel', 'pickleball', 'squash'].includes(sport)) return memberText("portalPresentation.member__sport_open_match", { sport: formatSportTypeLabel(sport, memberText) })
      return memberText('portalPresentation.member_racquet_open_match')
    }
    const sourceKeys = {"spa":"portalPresentation.departments.spa","fitness":"portalPresentation.departments.fitness","racquet":"portalPresentation.departments.racquet","hotel":"portalPresentation.member_hotel_reservation","rooms":"portalPresentation.member_hotel_reservation","events":"portalPresentation.member_event","event":"portalPresentation.member_event","dining":"portalPresentation.member_dining","reciprocal":"portalPresentation.member_reciprocal_access","academy":"portalPresentation.member_academy"}
    if (sourceKeys[booking?.source]) return memberText(sourceKeys[booking.source])
    const type = String(booking?.type || booking?.source || booking?.tag || memberText("portalPresentation.member_reservation"))
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  const getBookingVenue = (booking) => (
    bookingDetailValue(booking, 'locationName', 'location_name', 'course_name', 'courseName', 'court_name', 'courtName', 'service_name', 'serviceName', 'venue_name', 'venueName', 'subtitle', 'detailLine', 'detail_line') || getBookingTitle(booking) || memberText("portalPresentation.member_club_reservation")
  )

  const getBookingStart = (booking) => bookingDetailValue(booking, 'startAt', 'start_at', 'tee_time', 'slot_time')

  const getBookingTitle = (booking) => {
    const title = bookingDetailValue(booking, 'title', 'name')
    if (title) return title
    return formatBookingType(booking)
  }

  const isUuidLike = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || '').trim())

  const getMemberFacingBookingReference = (booking) => {
    const reference = bookingDetailValue(booking, 'bookingReference', 'booking_reference')
    if (!reference || isUuidLike(reference)) return null
    return reference
  }

  const getBookingCost = (booking) => {
    const value = bookingDetailValue(booking, 'total_cost', 'totalCost', 'price_total', 'amount_due', 'cost', 'price')
    const numeric = Number(value)
    if (!Number.isFinite(numeric) || numeric <= 0) return null
    return formatDashboardCurrency(numeric, { locale, code: booking?.currency || currencyCode, symbol: currencySymbol })
  }

  const bookingIconConfig = (booking) => {
    if (isGolfUpcomingBooking(booking)) return { icon: Flag, className: 'mp-row-icon mp-row-icon--golf' }
    const type = booking?.type || booking?.source || ''
    if(type==='academy')return {icon:GraduationCap,className:'mp-row-icon'};
    if (type === 'reciprocal_club_visit' || type === 'reciprocal_visit') {
      return { icon: Globe, className: 'mp-row-icon mp-row-icon--reciprocal' }
    }
    return { icon: Ticket, className: 'mp-row-icon' }
  }

  const getBookingImage = (booking) => (
    bookingDetailValue(
      booking,
      'courseImageUrl',
      'course_image_url',
      'courseImage',
      'course_image',
      'facilityImageUrl',
      'facility_image_url',
      'facilityImage',
      'facility_image',
      'imageUrl',
      'image_url',
      'bookingTypeImageUrl',
      'booking_type_image_url',
      'typeImageUrl',
      'type_image_url',
      'photoUrl',
      'photo_url',
      'thumbnailUrl',
      'thumbnail_url'
    ) || null
  )

  const renderBookingRow = (booking, { detailed = false, carouselIndex = null } = {}) => {
    const bookingType = formatBookingType(booking)
    const venue = getBookingVenue(booking)
    const start = getBookingStart(booking)
    const isSelected = Boolean(upcomingDetailIdentity(booking)) && upcomingDetailIdentity(booking) === selectedUpcomingIdentityRef.current
    const isCancellable = Boolean(booking?.can_cancel || booking?.canCancel)
    const status = bookingDetailValue(booking, 'status')
    const cost = getBookingCost(booking)
    const bookingImage = getBookingImage(booking)
    const openMatch = booking?.openMatch || null
    const confirmedPlayers = Number(openMatch?.confirmed_count || 0)
    const maxPlayers = Number(openMatch?.max_players || 0)
    const placesAvailable = Number(openMatch?.places_available || Math.max(0, maxPlayers - confirmedPlayers))
    const rowTitle = getBookingTitle(booking) || (isGolfUpcomingBooking(booking) && booking?.holes ? `Golf – ${booking.holes} Holes` : null) || bookingType || memberText("portalPresentation.member_reservation")
    const openFromRow = () => {
      if (!detailed) openAllReservationsModal()
      openUpcomingBooking(booking)
    }

    return (
      <button
        key={upcomingDetailIdentity(booking) || `${booking.title}-${start}`}
        type="button"
        className={`mp-panel-row mp-reservation-card mp-upcoming-booking-row${detailed ? ' mp-reservation-card--detailed' : ' mp-reservation-card--compact'}${isSelected ? ' mp-reservation-card--selected' : ''}`}
        onClick={openFromRow}
        aria-pressed={isSelected ? 'true' : 'false'}
        data-carousel-index={carouselIndex ?? undefined}
      >
        <BookingThumbnail booking={booking} src={bookingImage} />
        <span className="mp-reservation-card__content mp-upcoming-booking-row__content">
          {detailed ? <span className="mp-reservation-card__eyebrow">{bookingType}</span> : null}
          <span className="mp-row-title mp-upcoming-booking-row__title">{rowTitle}</span>
          <span className="mp-upcoming-booking-row__meta mp-upcoming-booking-row__meta--inline">
            <span><CalendarClock size={12} aria-hidden="true" />{formatDateOnly(start)}</span>
            <span><Clock3 size={12} aria-hidden="true" />{formatTimeOnly(start)}</span>
            {cost ? <span>{cost}</span> : null}
          </span>
          <span className="mp-upcoming-booking-row__meta mp-upcoming-booking-row__location">
            <span><MapPin size={12} aria-hidden="true" />{venue}</span>
          </span>
          {openMatch ? <span className="mp-upcoming-booking-row__meta"><span><UsersRound size={12} aria-hidden="true" />{confirmedPlayers}/{maxPlayers} {memberText("portalPresentation.member_players")} {memberText("portalPresentation.member_places_available_count", { count: placesAvailable })}</span></span> : null}
        </span>
        {detailed ? (
          <span className="mp-reservation-card__badges">
            <span className="mp-status-pill">{formatFacilityBookingStatus(status, memberText)}</span>
            {isCancellable ? <span className="mp-status-pill mp-status-pill--success">{memberText("portalPresentation.member_can_cancel")}</span> : null}
          </span>
        ) : (
          <ChevronRight className="mp-upcoming-booking-row__chevron" size={18} aria-hidden="true" />
        )}
      </button>
    )
  }



  const renderClubNewsRow = (item) => {
    const isPdf = item?.announcement_type === 'upload'
    const preview = isPdf ? (item?.original_filename || memberText("portalPresentation.member_pdf_announcement")) : (item?.content || '').slice(0, 120)
    return (
      <button key={item.id} type="button" className="mp-panel-row mp-reservation-card mp-reservation-card--compact" onClick={() => openClubNews(item)}>
        <span className="mp-reservation-card__icon" aria-hidden="true"><FileText size={18} className="mp-row-icon" /></span>
        <span className="mp-reservation-card__content">
          <span className="mp-row-title">{item.title}</span>
          <span className="mp-reservation-card__meta"><span>{preview}</span>{isPdf ? <span>PDF</span> : null}</span>
        </span>
      </button>
    )
  }

  const isRacquetUpcomingBooking = booking => bookingDomain(booking) === 'racquet'
  const isDiningUpcomingBooking = booking => bookingDomain(booking) === 'dining'
  const isGolfUpcomingBooking = booking => bookingDomain(booking) === 'golf'
  const isSpaUpcomingBooking = booking => bookingDomain(booking) === 'spa'
  const isFitnessUpcomingBooking = booking => bookingDomain(booking) === 'fitness'
  const isHotelUpcomingBooking = booking => bookingDomain(booking) === 'hotel'

  function bookingDetailValue(booking, ...keys) {
    for (const key of keys) {
      const value = booking?.[key]
      if (value !== undefined && value !== null && value !== '') return value
    }
    return null
  }

  const normalizeUpcomingBookingDetails = (summary = {}, detail = {}) => {
    const value = (...keys) => bookingDetailValue(detail, ...keys) ?? bookingDetailValue(summary, ...keys)
    const openMatch = Object.hasOwn(detail, 'open_match') ? detail.open_match : value('openMatch', 'open_match') || null
    const startAt = value('start_at', 'startAt', 'tee_time', 'slot_time')
    const durationMinutes = value('duration_minutes', 'durationMinutes', 'duration')
    const startTimestamp = new Date(startAt).getTime()
    const endAt = value('end_at', 'endAt') || (Number.isFinite(startTimestamp) && Number(durationMinutes) > 0
      ? new Date(startTimestamp + Number(durationMinutes) * 60000).toISOString()
      : null)
    return {
      ...summary,
      ...detail,
      id: canonicalBookingId(summary),
      booking_id: value('booking_id', 'bookingId', 'id') || null,
      source: bookingDomain(summary),
      type: value('type', 'booking_type', 'source') || null,
      sport_type: value('sport_type', 'sportType', 'sport') || null,
      title: value('title', 'name') || null,
      court_name: value('court_name', 'courtName', 'locationName', 'location_name', 'subtitle', 'title') || null,
      venue_name: value('venue_name', 'venueName', 'locationName', 'location_name') || null,
      start_at: startAt || null,
      end_at: endAt,
      duration_minutes: durationMinutes || null,
      price_total: value('price_total', 'total_cost', 'totalCost', 'amount_due', 'cost', 'price'),
      total_cost: value('total_cost', 'totalCost', 'price_total', 'amount_due', 'cost', 'price'),
      currency: value('currency', 'currency_code', 'currencyCode') || summary?.currency || null,
      status: value('status', 'booking_status') || 'upcoming',
      payment_status: value('payment_status', 'paymentStatus') || null,
      payment_method: value('payment_method', 'paymentMethod', 'booking_mode') || null,
      openMatch,
      open_match: openMatch,
      match_type: value('match_type', 'matchType') || openMatch?.match_type || null,
      join_policy: value('join_policy', 'joinPolicy') || openMatch?.join_policy || null,
      confirmed_count: value('confirmed_count', 'confirmedCount') ?? openMatch?.confirmed_count ?? null,
      max_players: value('max_players', 'maxPlayers', 'capacity') ?? openMatch?.max_players ?? null,
      places_available: value('places_available', 'placesAvailable') ?? openMatch?.places_available ?? null,
      players: value('players', 'participants') || [],
      image_url: value('image_url', 'imageUrl', 'facilityImageUrl', 'facility_image_url', 'bookingTypeImageUrl', 'booking_type_image_url') || null,
    }
  }

  const normalizeGolfBookingDetails = (booking = {}) => ({
    ...booking,
    id: bookingDetailValue(booking, 'id', 'booking_id', 'bookingId') || null,
    name: bookingDetailValue(booking, 'name', 'title') || memberText("portalPresentation.member_golf_booking_caption"),
    title: bookingDetailValue(booking, 'title', 'name') || memberText("portalPresentation.member_tee_time"),
    type: bookingDetailValue(booking, 'type') || 'golf',
    course_name: bookingDetailValue(booking, 'course_name', 'courseName', 'subtitle') || null,
    start_at: bookingDetailValue(booking, 'start_at', 'startAt', 'tee_time', 'slot_time') || null,
    tee_time: bookingDetailValue(booking, 'tee_time', 'start_at', 'startAt', 'slot_time') || null,
    holes: bookingDetailValue(booking, 'holes') || null,
    players: Array.isArray(booking?.players) ? booking.players : [],
    addons: Array.isArray(booking?.addons) ? booking.addons : [],
    total_cost: bookingDetailValue(booking, 'total_cost', 'totalCost', 'price_total', 'amount_due') || 0,
    payment_method: bookingDetailValue(booking, 'payment_method', 'paymentMethod', 'booking_mode') || null,
    booking_reference: bookingDetailValue(booking, 'booking_reference', 'bookingReference', 'id', 'booking_id', 'bookingId') || null,
    status: bookingDetailValue(booking, 'status') || 'booked',
    can_cancel: bookingDetailValue(booking, 'can_cancel', 'canCancel') === true,
    cancellation_disabled_reason: bookingDetailValue(booking, 'cancellation_disabled_reason', 'cancellationDisabledReason') || null,
    cancellation_cutoff_hours: bookingDetailValue(booking, 'cancellation_cutoff_hours', 'cancellationCutoffHours'),
    cancellation_deadline_at: bookingDetailValue(booking, 'cancellation_deadline_at', 'cancellationDeadlineAt') || null,
    member_portal_cancellations_enabled: bookingDetailValue(booking, 'member_portal_cancellations_enabled', 'memberPortalCancellationsEnabled') === true,
  })

  const upcomingDetailIdentity = booking => bookingIdentityKey(booking, { club: clubCode, member: member?.id || member?.member_id || displayCacheScopeKey })

  const fetchUpcomingBookingDetail = (booking) => {
    const identity = upcomingDetailIdentity(booking)
    if (!identity) return Promise.resolve(null)
    if (upcomingDetailCacheRef.current.has(identity)) return Promise.resolve(upcomingDetailCacheRef.current.get(identity))
    if (upcomingDetailInflightRef.current.has(identity)) return upcomingDetailInflightRef.current.get(identity)

    const sessionGeneration = upcomingDetailSessionRef.current
    const bookingId = getUpcomingBookingId(booking)
    let path
    let project
    if (isHotelUpcomingBooking(booking)) {
      path = `/api/members-portal/${clubCode}/hotel/reservations/${bookingId}`
      project = (json) => ({ kind: 'hotel', data: { ...booking, ...json.data?.reservation, id: bookingId, source: 'hotel', type: 'hotel_reservation', total_cost: json.data?.reservation?.total_cost ?? booking.total_cost ?? booking.totalCost, cancellation_policy: json.data?.cancellation_policy, can_cancel: json.data?.can_cancel === true, cancellation_disabled_reason: json.data?.cancellation_disabled_reason || null } })
    } else if (isSpaUpcomingBooking(booking)) {
      path = `/api/members-portal/${clubCode}/spa/bookings/${bookingId}`
      project = (json) => ({ kind: 'spa', data: { ...booking, ...json.data, id: bookingId, source: 'spa', type: 'spa' } })
    } else if (isRacquetUpcomingBooking(booking)) {
      path = `/api/members-portal/${clubCode}/racquet/bookings/${bookingId}`
      project = (json) => ({ kind: 'racquet', data: normalizeUpcomingBookingDetails(booking, json.data) })
    } else if (isFitnessUpcomingBooking(booking)) {
      path = `/api/members-portal/${clubCode}/fitness/bookings/${bookingId}`
      project = (json) => ({ kind: 'fitness', data: { ...booking, ...json.data, id: bookingId, source: 'fitness', type: 'fitness' } })
    } else if (isGolfUpcomingBooking(booking)) {
      path = `/api/members-portal/${clubCode}/golf/bookings/${bookingId}`
      project = (json) => ({ kind: 'golf', data: normalizeGolfBookingDetails({ ...booking, ...json.data, id: bookingId, bookingId, source: 'golf' }) })
    } else return Promise.resolve({ kind: 'generic', data: booking })

    const request = Promise.resolve().then(() => memberPortalApiGet(path)).then(async (res) => {
      const json = await res.json()
      if (!res.ok || !json?.ok) return null
      const detail = project(json)
      if (upcomingDetailSessionRef.current !== sessionGeneration) return null
      upcomingDetailCacheRef.current.set(identity, detail)
      return detail
    }).catch(() => null).finally(() => {
      if (upcomingDetailInflightRef.current.get(identity) === request) upcomingDetailInflightRef.current.delete(identity)
    })
    upcomingDetailInflightRef.current.set(identity, request)
    return request
  }

  const applyUpcomingDetail = (detail) => {
    if (!detail) return
    if (detail.kind === 'golf') {
      setSelectedBooking(null)
      setSelectedGolfBooking(detail.data)
    } else {
      setSelectedGolfBooking(null)
      setSelectedBooking(detail.data)
    }
  }

  const prefetchAdjacentUpcomingDetails = (booking) => {
    const index = safeBookings.findIndex((candidate) => upcomingDetailIdentity(candidate) === upcomingDetailIdentity(booking))
    if (index < 0) return
    for (const adjacent of [safeBookings[index - 1], safeBookings[index + 1]]) {
      if (adjacent) void fetchUpcomingBookingDetail(adjacent)
    }
  }

  const openUpcomingBooking = async (booking, { prefetch = true } = {}) => {
    if((booking?.source||booking?.type)==='academy'){navigate(`/${clubCode}/academy?order=${booking.bookingId||booking.booking_id||booking.id}`);return;}
    const requestAuthority = ++upcomingDetailRequestRef.current
    setUpcomingDetailError('')
    setGolfCancelError('')
    setGolfCancelSuccess('')
    setRacquetCancelError('')
    setRacquetCancelSuccess('')
    setSpaCancelError('')
    setSpaCancelSuccess('')
    setFitnessCancelError('')
    setFitnessCancelSuccess('')
    setHotelCancelError('')
    setHotelCancelSuccess('')
    setGolfCancelConfirmOpen(false)
    setRacquetCancelConfirmOpen(false)
    setSpaCancelConfirmOpen(false)
    setFitnessCancelConfirmOpen(false)
    setHotelCancelConfirmOpen(false)
    selectedUpcomingIdentityRef.current = upcomingDetailIdentity(booking)
    upcomingCarouselBookingIdRef.current = selectedUpcomingIdentityRef.current
    setUpcomingCarouselIndex(Math.max(0, safeBookings.findIndex(item => upcomingDetailIdentity(item) === upcomingDetailIdentity(booking))))
    setSelectedUpcomingBooking(booking)

    // The canonical summary is the stable shell. Never blank the useful detail
    // area while hydration is in flight.
    if (isGolfUpcomingBooking(booking)) {
      setSelectedBooking(null)
      setSelectedGolfBooking(normalizeGolfBookingDetails(booking))
    } else {
      setSelectedGolfBooking(null)
      setSelectedBooking(normalizeUpcomingBookingDetails(booking))
    }
    setSelectedBookingLoading(true)

    if (prefetch) prefetchAdjacentUpcomingDetails(booking)
    const cached = upcomingDetailCacheRef.current.get(upcomingDetailIdentity(booking))
    if (cached) { applyUpcomingDetail(cached); setSelectedBookingLoading(false) }
    else {
      const detail = await fetchUpcomingBookingDetail(booking)
      if (upcomingDetailRequestRef.current === requestAuthority) {
        applyUpcomingDetail(detail)
        setSelectedBookingLoading(false)
        if (!detail) setUpcomingDetailError('failed')
      }
    }
  }

  const detailScope = JSON.stringify([clubCode, member?.id || member?.member_id, displayCacheScopeKey])
  useEffect(() => {
    upcomingDetailRequestRef.current += 1
    upcomingDetailSessionRef.current += 1
    upcomingDetailCacheRef.current.clear()
    upcomingDetailInflightRef.current.clear()
    selectedUpcomingIdentityRef.current = null
    setSelectedUpcomingBooking(null)
    setSelectedBooking(null)
    setSelectedGolfBooking(null)
    setUpcomingDetailError('')
    setSelectedBookingLoading(false)
    setGolfCancelConfirmOpen(false)
    setRacquetCancelConfirmOpen(false)
    setSpaCancelConfirmOpen(false)
    setFitnessCancelConfirmOpen(false)
    setHotelCancelConfirmOpen(false)
    return () => { upcomingDetailRequestRef.current += 1; upcomingDetailSessionRef.current += 1 }
  }, [detailScope])

  useEffect(() => {
    if (isAllReservationsOpen) return
    upcomingDetailRequestRef.current += 1
    upcomingDetailSessionRef.current += 1
    upcomingDetailCacheRef.current.clear()
    upcomingDetailInflightRef.current.clear()
    selectedUpcomingIdentityRef.current = null
    setSelectedUpcomingBooking(null)
    setSelectedBooking(null)
    setSelectedGolfBooking(null)
    setUpcomingDetailError('')
    setSelectedBookingLoading(false)
    setGolfCancelConfirmOpen(false)
    setRacquetCancelConfirmOpen(false)
    setSpaCancelConfirmOpen(false)
    setFitnessCancelConfirmOpen(false)
    setHotelCancelConfirmOpen(false)
    openedNavigationBookingRef.current = null
    navigationBookingRefreshRef.current = null
  }, [isAllReservationsOpen])

  // Selection belongs to a typed record, never to its position in a refreshed list.
  useEffect(() => {
    if (!isAllReservationsOpen || upcomingLoading) return
    const identity = selectedUpcomingIdentityRef.current
    if (selectedUpcomingBooking && !identity) return
    if (!identity && upcomingDetailError === 'unavailable') return
    const index = identity ? safeBookings.findIndex(item => upcomingDetailIdentity(item) === identity) : 0
    if (index < 0) {
      upcomingDetailRequestRef.current += 1
      selectedUpcomingIdentityRef.current = null
      setSelectedUpcomingBooking(null)
      setSelectedBooking(null)
      setSelectedGolfBooking(null)
      upcomingCarouselBookingIdRef.current = null
      setUpcomingDetailError('unavailable')
      setSelectedBookingLoading(false)
      return
    }
    if (identity && safeBookings[index] !== selectedUpcomingBooking) {
      upcomingDetailSessionRef.current += 1
      upcomingDetailCacheRef.current.clear()
      upcomingDetailInflightRef.current.clear()
      void openUpcomingBooking(safeBookings[index], { prefetch: false })
    }
    setUpcomingCarouselIndex(index)

  }, [isAllReservationsOpen, safeBookings, selectedUpcomingBooking, upcomingLoading, upcomingDetailError])

  // Layout can settle after opening (fonts, responsive breakpoint, native rotation).
  // Keep the selected record visible without treating resize as a new selection.
  useEffect(() => {
    if (!isAllReservationsOpen) return undefined
    const list = upcomingCarouselRef.current
    if (!list) return undefined
    const align = () => {
      const card = list.querySelector(`[data-carousel-index="${upcomingCarouselIndex}"]`)
      if (card) list.scrollTo?.({ left: card.offsetLeft - (list.firstElementChild?.offsetLeft || 0), behavior: 'auto' })
    }
    align()
    const observer = new ResizeObserver(align)
    observer.observe(list)
    return () => observer.disconnect()
  }, [isAllReservationsOpen, upcomingCarouselIndex])

  useEffect(() => {
    const targetId = location.state?.viewBookingId
    const targetKey = JSON.stringify([location.state?.viewBookingType, targetId])
    if (!targetId || openedNavigationBookingRef.current === targetKey) return
    const targets = safeBookings.filter((item) => String(getUpcomingBookingId(item)) === String(targetId) && (!location.state?.viewBookingType || bookingDomain(item) === bookingDomain({ source: location.state.viewBookingType })))
    const booking = targets.length === 1 ? targets[0] : null
    if (!booking) {
      if (navigationBookingRefreshRef.current !== targetKey) {
        navigationBookingRefreshRef.current = targetKey
        void onUpcomingBookingsRefresh?.()
      }
      return
    }
    openedNavigationBookingRef.current = targetKey
    openAllReservationsModal()
    void openUpcomingBooking(booking)

  }, [location.pathname, location.state?.viewBookingId, location.state?.viewBookingType, navigate, onUpcomingBookingsRefresh, safeBookings])

  const cancellationMessage = (booking) => {
    if (!booking) return ''
    if (booking.can_cancel) return ''
    if (booking.payment_method === 'pay_now' || booking.settlement_method === 'direct_payment' || booking.cancellation_disabled_reason === 'direct_payment_cancellation_requires_staff') return memberText("portalPresentation.member_this_booking_was_paid_online_please_contact_the_club_directly_if_you_wish_to_cancel_or_discuss_a_refund")
    if (booking.cancellation_disabled_reason === 'already_cancelled') return memberText("portalPresentation.member_this_booking_has_already_been_cancelled")
    if (booking.cancellation_disabled_reason === 'cutoff_passed') {
      return memberText("portalPresentation.member_the_online_cancellation_period_has_passed_please_contact_the_club")
    }
    return memberText("portalPresentation.member_online_cancellation_is_not_available_for_this_booking_please_contact_the_club")
  }

  const cancelGolfBooking = async () => {
    if (!selectedGolfBooking?.id) return
    setGolfCancelError('')
    setGolfCancelSuccess('')
    try {
      const bookingId = selectedGolfBooking.id
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/golf/bookings/${bookingId}/cancel`, {})
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) {
        const message = json?.error || "portalPresentation.member_unable_to_cancel_booking"
        setGolfCancelError(message)
        setGolfCancelErrorToast(message)
        setGolfCancelErrorToastKey((key) => key + 1)
        setGolfCancelConfirmOpen(false)
        return
      }
      setGolfCancelConfirmOpen(false)
      setSelectedGolfBooking(null)
      selectedUpcomingIdentityRef.current = null
      setSelectedUpcomingBooking(null)
      setCancelledUpcomingBookingIds((previous) => new Set([...previous, upcomingDetailIdentity(selectedUpcomingBooking)]))
      setGolfCancelSuccess("portalPresentation.member_your_tee_time_has_been_cancelled")
      setGolfCancelSuccessKey((key) => key + 1)
      if (typeof onUpcomingBookingsRefresh === 'function') {
        await onUpcomingBookingsRefresh()
      }
    } catch (error) {
      const message = "portalPresentation.member_unable_to_cancel_booking"
      setGolfCancelError(message)
      setGolfCancelErrorToast(message)
      setGolfCancelErrorToastKey((key) => key + 1)
      setGolfCancelConfirmOpen(false)
    }
  }


  const fitnessCancellationMessage = (booking) => {
    if (!booking) return ''
    if (booking.can_cancel) return ''
    if (booking.payment_method === 'pay_now' || booking.settlement_method === 'direct_payment' || booking.cancellation_disabled_reason === 'direct_payment_cancellation_requires_staff') return memberText("portalPresentation.member_this_booking_was_paid_online_please_contact_the_club_directly_if_you_wish_to_cancel_or_discuss_a_refund")
    const reason = booking.cancellation_disabled_reason || booking.cancellation_policy?.error
    if (reason === 'cancellation_cutoff_passed' || reason === 'cutoff_passed') return memberText("portalPresentation.member_this_booking_is_inside_the_cancellation_notice_period_please_contact_the_club_to_cancel_this_booking")
    return memberText("portalPresentation.member_please_contact_the_club_to_cancel_this_booking")
  }

  const racquetCancellationMessage = (booking) => {
    if (!booking) return ''
    if (booking.can_cancel) return ''
    if (booking.payment_method === 'pay_now' || booking.settlement_method === 'direct_payment' || booking.cancellation_disabled_reason === 'direct_payment_cancellation_requires_staff') return memberText("portalPresentation.member_this_booking_was_paid_online_please_contact_the_club_directly_if_you_wish_to_cancel_or_discuss_a_refund")
    const reason = booking.cancellation_disabled_reason || booking.cancellation_policy?.error
    if (reason === 'manual_review_required' || reason === 'online_cancellation_disabled') return memberText("portalPresentation.member_please_contact_the_club_to_cancel_this_booking")
    if (reason === 'cancellation_cutoff_passed') return memberText("portalPresentation.member_this_booking_is_inside_the_cancellation_notice_period_please_contact_the_club_to_cancel_this_booking")
    return memberText("portalPresentation.member_please_contact_the_club_to_cancel_this_booking")
  }

  const racquetRefundOutcomeLabel = (booking) => {
    const policy = booking?.cancellation_policy
    if (!policy) return null
    const refund = Number(policy.expected_refund_amount || 0)
    const fee = Number(policy.expected_cancellation_fee_amount || 0)
    if (policy.refund_mode === 'full_refund') return memberText("portalPresentation.member_expected_refund_amount", { amount: formatDashboardCurrency(refund, { locale, code: booking?.currency || currencyCode, symbol: currencySymbol }) })
    if (policy.refund_mode === 'no_refund') return memberText("portalPresentation.member_refund_outcome_no_refund")
    if (policy.refund_mode === 'percentage_refund') return memberText("portalPresentation.member_expected_refund_amount", { amount: formatDashboardCurrency(refund, { locale, code: booking?.currency || currencyCode, symbol: currencySymbol }) })
    if (policy.refund_mode === 'fixed_fee') return memberText("portalPresentation.member_expected_refund_amount_cancellation_fee_fee", { amount: formatDashboardCurrency(refund, { locale, code: booking?.currency || currencyCode, symbol: currencySymbol }), fee: formatDashboardCurrency(fee, { locale, code: booking?.currency || currencyCode, symbol: currencySymbol }) })
    return memberText("portalPresentation.member_cancellation_requires_manual_review")
  }

  const cancelRacquetBooking = async () => {
    if (!selectedBooking?.id) return
    setRacquetCancelError('')
    try {
      const bookingId = selectedBooking.id
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/racquet/bookings/${bookingId}/cancel`, {})
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) {
        const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
        setRacquetCancelError(message)
        setRacquetCancelErrorToast(message)
        setRacquetCancelErrorToastKey((key) => key + 1)
        setRacquetCancelConfirmOpen(false)
        return
      }
      setRacquetCancelConfirmOpen(false)
      setSelectedBooking(null)
      selectedUpcomingIdentityRef.current = null
      setSelectedUpcomingBooking(null)
      setCancelledUpcomingBookingIds((previous) => new Set([...previous, upcomingDetailIdentity(selectedUpcomingBooking)]))
      setRacquetCancelSuccess("portalPresentation.member_your_racquet_booking_has_been_cancelled")
      setRacquetCancelSuccessKey((key) => key + 1)
      if (typeof onUpcomingBookingsRefresh === 'function') await onUpcomingBookingsRefresh()
    } catch (error) {
      const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
      setRacquetCancelError(message)
      setRacquetCancelErrorToast(message)
      setRacquetCancelErrorToastKey((key) => key + 1)
      setRacquetCancelConfirmOpen(false)
    }
  }

  const spaCancellationMessage = (booking) => {
    if (!booking) return ''
    if (booking.can_cancel) return ''
    if (booking.payment_method === 'pay_now' || booking.settlement_method === 'direct_payment' || booking.cancellation_disabled_reason === 'direct_payment_cancellation_requires_staff') return memberText("portalPresentation.member_this_booking_was_paid_online_please_contact_the_club_directly_if_you_wish_to_cancel_or_discuss_a_refund")
    const reason = booking.cancellation_disabled_reason || booking.cancellation_policy?.error
    if (reason === 'cancellation_cutoff_passed') return memberText("portalPresentation.member_this_booking_is_inside_the_cancellation_notice_period_please_contact_the_club_to_cancel_this_booking")
    return memberText("portalPresentation.member_please_contact_the_club_to_cancel_this_booking")
  }

  const cancelSpaBooking = async () => {
    if (!selectedBooking?.id) return
    setSpaCancelError('')
    try {
      const bookingId = selectedBooking.id
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/spa/bookings/${bookingId}/cancel`, {})
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) {
        const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
        setSpaCancelError(message)
        setSpaCancelErrorToast(message)
        setSpaCancelErrorToastKey((key) => key + 1)
        setSpaCancelConfirmOpen(false)
        return
      }
      setSpaCancelConfirmOpen(false)
      setSelectedBooking(null)
      selectedUpcomingIdentityRef.current = null
      setSelectedUpcomingBooking(null)
      setCancelledUpcomingBookingIds((previous) => new Set([...previous, upcomingDetailIdentity(selectedUpcomingBooking)]))
      setSpaCancelSuccess("portalPresentation.member_your_spa_booking_has_been_cancelled")
      setSpaCancelSuccessKey((key) => key + 1)
      if (typeof onUpcomingBookingsRefresh === 'function') await onUpcomingBookingsRefresh()
    } catch (error) {
      const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
      setSpaCancelError(message)
      setSpaCancelErrorToast(message)
      setSpaCancelErrorToastKey((key) => key + 1)
      setSpaCancelConfirmOpen(false)
    }
  }

  const cancelFitnessBooking = async () => {
    if (!selectedBooking?.id) return
    setFitnessCancelError('')
    try {
      const bookingId = selectedBooking.id
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/fitness/bookings/${bookingId}/cancel`, {})
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) {
        const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
        setFitnessCancelError(message)
        setFitnessCancelErrorToast(message)
        setFitnessCancelErrorToastKey((key) => key + 1)
        setFitnessCancelConfirmOpen(false)
        return
      }
      setFitnessCancelConfirmOpen(false)
      setSelectedBooking(null)
      selectedUpcomingIdentityRef.current = null
      setSelectedUpcomingBooking(null)
      setCancelledUpcomingBookingIds((previous) => new Set([...previous, upcomingDetailIdentity(selectedUpcomingBooking)]))
      setFitnessCancelSuccess("portalPresentation.member_your_fitness_booking_has_been_cancelled")
      setFitnessCancelSuccessKey((key) => key + 1)
      if (typeof onUpcomingBookingsRefresh === 'function') await onUpcomingBookingsRefresh()
    } catch (error) {
      const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
      setFitnessCancelError(message)
      setFitnessCancelErrorToast(message)
      setFitnessCancelErrorToastKey((key) => key + 1)
      setFitnessCancelConfirmOpen(false)
    }
  }

  const hotelCancellationMessage = (booking) => {
    if (!booking) return ''
    if (booking.can_cancel) return ''
    if (booking.payment_method === 'pay_now' || booking.settlement_method === 'direct_payment' || booking.cancellation_disabled_reason === 'direct_payment_cancellation_requires_staff') return memberText("portalPresentation.member_this_booking_was_paid_online_please_contact_the_club_directly_if_you_wish_to_cancel_or_discuss_a_refund")
    const reason = booking.cancellation_disabled_reason || booking.cancellation_policy?.error
    if (reason === 'cancellation_cutoff_passed') return memberText("portalPresentation.member_this_stay_is_inside_the_cancellation_notice_period_please_contact_the_club_to_cancel_this_booking")
    return memberText("portalPresentation.member_please_contact_the_club_to_cancel_this_booking")
  }

  const cancelHotelBooking = async () => {
    if (!selectedBooking?.id) return
    setHotelCancelError('')
    try {
      const bookingId = selectedBooking.id
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/hotel/reservations/${bookingId}/cancel`, {})
      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) {
        const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
        setHotelCancelError(message)
        setHotelCancelErrorToast(message)
        setHotelCancelErrorToastKey((key) => key + 1)
        setHotelCancelConfirmOpen(false)
        return
      }
      setHotelCancelConfirmOpen(false)
      setSelectedBooking(null)
      selectedUpcomingIdentityRef.current = null
      setSelectedUpcomingBooking(null)
      setCancelledUpcomingBookingIds((previous) => new Set([...previous, upcomingDetailIdentity(selectedUpcomingBooking)]))
      setHotelCancelSuccess("portalPresentation.member_your_hotel_booking_has_been_cancelled")
      setHotelCancelSuccessKey((key) => key + 1)
      if (typeof onUpcomingBookingsRefresh === 'function') await onUpcomingBookingsRefresh()
    } catch (error) {
      const message = "portalPresentation.member_unable_to_cancel_booking_please_contact_the_club"
      setHotelCancelError(message)
      setHotelCancelErrorToast(message)
      setHotelCancelErrorToastKey((key) => key + 1)
      setHotelCancelConfirmOpen(false)
    }
  }

  const selectedRacquetBooking = selectedUpcomingBooking && isRacquetUpcomingBooking(selectedUpcomingBooking) && selectedBooking ? selectedBooking : null
  const selectedSpaBooking = selectedUpcomingBooking && isSpaUpcomingBooking(selectedUpcomingBooking) && selectedBooking ? selectedBooking : null
  const selectedFitnessBooking = selectedUpcomingBooking && isFitnessUpcomingBooking(selectedUpcomingBooking) && selectedBooking ? selectedBooking : null
  const selectedHotelBooking = selectedUpcomingBooking && isHotelUpcomingBooking(selectedUpcomingBooking) && selectedBooking ? selectedBooking : null
  const selectedGenericBooking = selectedUpcomingBooking && !selectedGolfBooking && !selectedRacquetBooking && !selectedSpaBooking && !selectedFitnessBooking && !selectedHotelBooking && !isGolfUpcomingBooking(selectedUpcomingBooking) && !isRacquetUpcomingBooking(selectedUpcomingBooking) && !isSpaUpcomingBooking(selectedUpcomingBooking) && !isFitnessUpcomingBooking(selectedUpcomingBooking) && !isHotelUpcomingBooking(selectedUpcomingBooking) ? selectedUpcomingBooking : null
  const selectedStart = selectedGolfBooking?.start_at || selectedGolfBooking?.tee_time || selectedRacquetBooking?.start_at || selectedSpaBooking?.start_at || selectedFitnessBooking?.start_at || selectedHotelBooking?.check_in_date || selectedHotelBooking?.startAt || getBookingStart(selectedGenericBooking)
  const selectedCost = selectedGolfBooking
  ? formatDashboardCurrency(Number(selectedGolfBooking.total_cost || selectedGolfBooking.price_total || 0), { locale, code: selectedGolfBooking.currency || currencyCode, symbol: currencySymbol })
  : selectedRacquetBooking ? formatDashboardCurrency(Number(selectedRacquetBooking.price_total || selectedRacquetBooking.total_cost || 0), { locale, code: selectedRacquetBooking.currency || currencyCode, symbol: currencySymbol })
  : selectedSpaBooking ? formatDashboardCurrency(Number(selectedSpaBooking.price_total || selectedSpaBooking.total_cost || 0), { locale, code: selectedSpaBooking.currency || currencyCode, symbol: currencySymbol })
  : selectedFitnessBooking ? formatDashboardCurrency(Number(selectedFitnessBooking.price_total || selectedFitnessBooking.total_cost || selectedFitnessBooking.cost || 0), { locale, code: selectedFitnessBooking.currency || currencyCode, symbol: currencySymbol })
  : selectedHotelBooking ? formatDashboardCurrency(Number(selectedHotelBooking.total_cost || selectedHotelBooking.totalCost || 0), { locale, code: selectedHotelBooking.currency || selectedHotelBooking.currency_code || currencyCode, symbol: currencySymbol })
  : selectedGenericBooking ? getBookingCost(selectedGenericBooking) : null

  const renderGolfDetailPanel = () => (
  <>
    <div className="mp-reservation-detail__scroll">
      <div className="mp-detail-hero">
        <div className="mp-detail-hero__icon"><Flag size={22} /></div>
        <div>
          <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_golf_booking_caption")}</div>
          <h3>{memberText("portalPresentation.member_golf_booking_caption")}</h3>
          <p>{selectedGolfBooking.course_name || memberText("portalPresentation.member_golf_course")}</p>
        </div>
        <span className="mp-status-pill mp-status-pill--strong">{formatFacilityBookingStatus(selectedGolfBooking.status || 'booked', memberText)}</span>
      </div>

      <div className="mp-detail-grid mp-detail-grid--premium">
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_date")}</span><strong>{formatDateOnly(selectedStart)}</strong></div>
        <div><Clock3 size={16} /><span>{memberText("facilityBooking.time")}</span><strong>{formatTimeOnly(selectedStart)}</strong></div>
        <div><MapPin size={16} /><span>{memberText("golf.print.course")}</span><strong>{selectedGolfBooking.course_name || '—'}</strong></div>
        <div><Flag size={16} /><span>{memberText("portalPresentation.member_holes_caption")}</span><strong>{selectedGolfBooking.holes || '—'}</strong></div>
        <div><UsersRound size={16} /><span>{memberText("portalPresentation.member_players_caption")}</span><strong>{(selectedGolfBooking.players || []).length || '—'}</strong></div>
        <div><ReceiptText size={16} /><span>{memberText("portalPresentation.member_total_cost")}</span><strong>{selectedCost}</strong></div>
        <div><CreditCard size={16} /><span>{memberText("facilityBooking.payment")}</span><strong>{selectedGolfBooking.payment_method || memberText("portalPresentation.member_member_account_club_billing")}</strong></div>
      </div>

      <div className="mp-detail-section-grid">
        <section className="mp-detail-section-card">
          <div className="mp-detail-section-card__title"><UsersRound size={16} /> {memberText("portalPresentation.member_players_caption")}</div>
          {(selectedGolfBooking.players || []).length ? (selectedGolfBooking.players || []).map((player, idx) => (
            <div className="mp-mini-row" key={player.id || idx}><span>{player.player_name || player.name || memberText("portalPresentation.member_player_caption")}</span><strong>{player.player_type || 'member'}</strong></div>
          )) : <div className="mp-empty">{memberText("portalPresentation.member_no_players_listed")}</div>}
        </section>
        <section className="mp-detail-section-card">
          <div className="mp-detail-section-card__title"><ReceiptText size={16} /> {memberText("portalPresentation.member_add_ons")}</div>
          {(selectedGolfBooking.addons || []).length ? (selectedGolfBooking.addons || []).map((addon, idx) => (
            <div className="mp-mini-row" key={addon.id || idx}><span>{addon.name || 'Add-on'}</span><strong>{memberText("portalPresentation.member_qty")} {addon.quantity || 1}</strong></div>
          )) : <div className="mp-empty">{memberText("portalPresentation.member_no_add_ons")}</div>}
        </section>
      </div>

      <section className={`mp-policy-card${selectedGolfBooking.can_cancel ? ' mp-policy-card--success' : ''}`}>
        <div className="mp-detail-section-card__title"><ShieldCheck size={16} /> {memberText("portalPresentation.member_cancellation_policy_status")}</div>
        {selectedGolfBooking.can_cancel ? (
          <p>{memberText("portalPresentation.member_you_may_cancel_this_booking_online_under_the_club_s_cancellation_policy")}</p>
        ) : (
          <p>{cancellationMessage(selectedGolfBooking)}</p>
        )}
        {selectedGolfBooking.cancellation_cutoff_hours !== undefined && selectedGolfBooking.cancellation_cutoff_hours !== null ? (
          <div className="mp-row-sub">{memberText("portalPresentation.member_cutoff")} {selectedGolfBooking.cancellation_cutoff_hours} {memberText("portalPresentation.member_hours_before_tee_time")}</div>
        ) : null}
        {selectedGolfBooking.cancellation_deadline_at ? (
          <div className="mp-row-sub">{memberText("portalPresentation.member_deadline")} {formatDateTime(selectedGolfBooking.cancellation_deadline_at)}</div>
        ) : null}
      </section>
      {golfCancelError ? <div className="mp-empty mp-empty--error" role="alert">{memberText(golfCancelError)}</div> : null}
    </div>
    <div className="mp-reservation-detail__footer mp-reservation-detail__footer--actions-only">
      {selectedGolfBooking.can_cancel ? <button type="button" className="mp-primary mp-primary--danger" onClick={() => setGolfCancelConfirmOpen(true)}>{memberText("portalPresentation.member_cancel_booking")}</button> : null}
    </div>
  </>
  )


  const renderRacquetDetailPanel = () => (
  <>
    <div className="mp-reservation-detail__scroll">
      <div className="mp-detail-hero">
        <div className="mp-detail-hero__icon"><Ticket size={22} /></div>
        <div>
          <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_racquet_booking_caption")}</div>
          <h3>{selectedRacquetBooking.sport_type ? memberText(`racquetPlay.${selectedRacquetBooking.sport_type}`) : memberText("portalPresentation.member_racquet_booking_caption")}{selectedRacquetBooking.open_match ? ` · ${memberText("portalPresentation.member_open_match")}` : ''}</h3>
          <p>{selectedRacquetBooking.court_name || memberText("racquetPlay.court")}</p>
        </div>
        <span className="mp-status-pill mp-status-pill--strong">{formatFacilityBookingStatus(selectedRacquetBooking.status || 'booked', memberText)}</span>
      </div>
      <div className="mp-detail-grid mp-detail-grid--premium">
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_date")}</span><strong>{formatDateOnly(selectedStart)}</strong></div>
        <div><Clock3 size={16} /><span>{memberText("facilityBooking.time")}</span><strong>{formatTimeOnly(selectedStart)}</strong></div>
        <div><MapPin size={16} /><span>{memberText("racquetPlay.court")}</span><strong>{selectedRacquetBooking.court_name || '—'}</strong></div>
        <div><ReceiptText size={16} /><span>{memberText("portalPresentation.member_total_cost")}</span><strong>{selectedCost}</strong></div>
      </div>
      {selectedRacquetBooking.open_match ? <div className="mp-open-match-card__chips"><span className="mp-chip subtle">{selectedRacquetBooking.open_match.confirmed_count}/{selectedRacquetBooking.open_match.max_players} {memberText('portalPresentation.member_players_2')}</span><PlayingRangeBadge sport={selectedRacquetBooking.sport_type} min={selectedRacquetBooking.open_match.skill_min} max={selectedRacquetBooking.open_match.skill_max} /></div> : null}
      <section className={`mp-policy-card${selectedRacquetBooking.can_cancel ? ' mp-policy-card--success' : ''}`}>
        <div className="mp-detail-section-card__title"><ShieldCheck size={16} /> {memberText("portalPresentation.member_cancellation_policy_status")}</div>
        {selectedRacquetBooking.cancellation_policy?.member_policy_text ? <p>{selectedRacquetBooking.cancellation_policy.member_policy_text}</p> : null}
        {selectedRacquetBooking.can_cancel ? <p>{memberText("portalPresentation.member_you_may_cancel_this_racquet_booking_through_member_portal_cancellation")}</p> : <p>{racquetCancellationMessage(selectedRacquetBooking)}</p>}
        {selectedRacquetBooking.cancellation_policy?.cancellation_deadline_at ? <div className="mp-row-sub">{memberText("portalPresentation.member_deadline")} {formatDateTime(selectedRacquetBooking.cancellation_policy.cancellation_deadline_at)}</div> : null}
        {racquetRefundOutcomeLabel(selectedRacquetBooking) ? <div className="mp-row-sub">{racquetRefundOutcomeLabel(selectedRacquetBooking)}</div> : null}
        {racquetCancelError ? <div className="mp-empty mp-empty--error" role="alert">{memberText(racquetCancelError)}</div> : null}
      </section>
    </div>
    <div className="mp-reservation-detail__footer mp-reservation-detail__footer--actions-only">
      {selectedRacquetBooking.can_cancel ? <button type="button" className="mp-primary mp-primary--danger" onClick={() => setRacquetCancelConfirmOpen(true)}>{memberText("portalPresentation.member_cancel_booking")}</button> : null}
    </div>
  </>
  )

  const renderSpaDetailPanel = () => (
  <>
    <div className="mp-reservation-detail__scroll">
      <div className="mp-detail-hero">
        <div className="mp-detail-hero__icon"><Ticket size={22} /></div>
        <div>
          <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_spa_booking")}</div>
          <h3>{selectedSpaBooking.service_name || memberText("portalPresentation.member_spa_booking")}</h3>
          <p>{selectedSpaBooking.room_name || selectedSpaBooking.therapist_name || memberText("portalPresentation.member_spa")}</p>
        </div>
        <span className="mp-status-pill mp-status-pill--strong">{formatFacilityBookingStatus(selectedSpaBooking.status || 'booked', memberText)}</span>
      </div>
      <div className="mp-detail-grid mp-detail-grid--premium">
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_date")}</span><strong>{formatDateOnly(selectedStart)}</strong></div>
        <div><Clock3 size={16} /><span>{memberText("facilityBooking.time")}</span><strong>{formatTimeOnly(selectedStart)}</strong></div>
        <div><MapPin size={16} /><span>{memberText("portalPresentation.member_service")}</span><strong>{selectedSpaBooking.service_name || memberText("portalPresentation.member_spa_service")}</strong></div>
        <div><ReceiptText size={16} /><span>{memberText("portalPresentation.member_total_cost")}</span><strong>{selectedCost}</strong></div>
      </div>
      <section className={`mp-policy-card${selectedSpaBooking.can_cancel ? ' mp-policy-card--success' : ''}`}>
        <div className="mp-detail-section-card__title"><ShieldCheck size={16} /> {memberText("portalPresentation.member_cancellation_policy_status")}</div>
        {selectedSpaBooking.cancellation_policy?.member_policy_text ? <p>{selectedSpaBooking.cancellation_policy.member_policy_text}</p> : null}
        {selectedSpaBooking.can_cancel ? <p>{memberText("portalPresentation.member_you_may_cancel_this_spa_booking_through_member_portal_cancellation")}</p> : <p>{spaCancellationMessage(selectedSpaBooking)}</p>}
        {selectedSpaBooking.cancellation_policy?.cancellation_deadline_at ? <div className="mp-row-sub">{memberText("portalPresentation.member_deadline")} {formatDateTime(selectedSpaBooking.cancellation_policy.cancellation_deadline_at)}</div> : null}
        {racquetRefundOutcomeLabel(selectedSpaBooking) ? <div className="mp-row-sub">{racquetRefundOutcomeLabel(selectedSpaBooking)}</div> : null}
        {spaCancelError ? <div className="mp-empty mp-empty--error" role="alert">{memberText(spaCancelError)}</div> : null}
      </section>
    </div>
    <div className="mp-reservation-detail__footer mp-reservation-detail__footer--actions-only">
      {selectedSpaBooking.can_cancel ? <button type="button" className="mp-primary mp-primary--danger" onClick={() => setSpaCancelConfirmOpen(true)}>{memberText("portalPresentation.member_cancel_booking")}</button> : null}
    </div>
  </>
  )

  const renderFitnessDetailPanel = () => (
  <>
    <div className="mp-reservation-detail__scroll">
      <div className="mp-detail-hero">
        <div className="mp-detail-hero__icon"><Ticket size={22} /></div>
        <div>
          <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_fitness_booking")}</div>
          <h3>{selectedFitnessBooking.class_name || selectedFitnessBooking.title || memberText("portalPresentation.member_fitness_class_caption")}</h3>
          <p>{selectedFitnessBooking.class_location || selectedFitnessBooking.subtitle || memberText("portalPresentation.member_fitness")}</p>
        </div>
        <span className="mp-status-pill mp-status-pill--strong">{formatFacilityBookingStatus(selectedFitnessBooking.status || 'booked', memberText)}</span>
      </div>
      <div className="mp-detail-grid mp-detail-grid--premium">
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_date")}</span><strong>{formatDateOnly(selectedStart)}</strong></div>
        <div><Clock3 size={16} /><span>{memberText("facilityBooking.time")}</span><strong>{formatTimeOnly(selectedStart)}</strong></div>
        <div><MapPin size={16} /><span>{memberText("facilityBooking.class")}</span><strong>{selectedFitnessBooking.class_name || selectedFitnessBooking.subtitle || memberText("portalPresentation.member_fitness_class")}</strong></div>
        <div><ReceiptText size={16} /><span>{memberText("portalPresentation.member_total_cost")}</span><strong>{selectedCost}</strong></div>
      </div>
      <section className={`mp-policy-card${selectedFitnessBooking.can_cancel ? ' mp-policy-card--success' : ''}`}>
        <div className="mp-detail-section-card__title"><ShieldCheck size={16} /> {memberText("portalPresentation.member_cancellation_policy_status")}</div>
        {selectedFitnessBooking.cancellation_policy?.member_policy_text ? <p>{selectedFitnessBooking.cancellation_policy.member_policy_text}</p> : null}
        {selectedFitnessBooking.can_cancel ? <p>{memberText("portalPresentation.member_you_may_cancel_this_fitness_booking_through_member_portal_cancellation")}</p> : <p>{fitnessCancellationMessage(selectedFitnessBooking)}</p>}
        {selectedFitnessBooking.cancellation_policy?.cancellation_deadline_at || selectedFitnessBooking.cancellation_deadline_at ? <div className="mp-row-sub">{memberText("portalPresentation.member_deadline")} {formatDateTime(selectedFitnessBooking.cancellation_policy?.cancellation_deadline_at || selectedFitnessBooking.cancellation_deadline_at)}</div> : null}
        {racquetRefundOutcomeLabel(selectedFitnessBooking) ? <div className="mp-row-sub">{racquetRefundOutcomeLabel(selectedFitnessBooking)}</div> : null}
        {fitnessCancelError ? <div className="mp-empty mp-empty--error" role="alert">{memberText(fitnessCancelError)}</div> : null}
      </section>
    </div>
    <div className="mp-reservation-detail__footer mp-reservation-detail__footer--actions-only">
      {selectedFitnessBooking.can_cancel ? <button type="button" className="mp-primary mp-primary--danger" onClick={() => setFitnessCancelConfirmOpen(true)}>{memberText("portalPresentation.member_cancel_booking")}</button> : null}
    </div>
  </>
  )

  const renderHotelDetailPanel = () => (
  <>
    <div className="mp-reservation-detail__scroll">
      <div className="mp-detail-hero">
        <div className="mp-detail-hero__icon"><Ticket size={22} /></div>
        <div>
          <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_hotel_rooms")}</div>
          <h3>{selectedHotelBooking.requested_room_type || selectedHotelBooking.subtitle || memberText("portalPresentation.member_accommodation")}</h3>
          <p>{selectedHotelBooking.detail_line || selectedHotelBooking.detailLine || memberText("portalPresentation.member_hotel_stay")}</p>
        </div>
        <span className="mp-status-pill mp-status-pill--strong">{formatFacilityBookingStatus(selectedHotelBooking.status || 'booked', memberText)}</span>
      </div>
      <div className="mp-detail-grid mp-detail-grid--premium">
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_check_in")}</span><strong>{formatDateOnly(selectedHotelBooking.check_in_date || selectedStart)}</strong></div>
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_check_out")}</span><strong>{formatDateOnly(selectedHotelBooking.check_out_date || selectedHotelBooking.endAt)}</strong></div>
        <div><MapPin size={16} /><span>{memberText("portalPresentation.member_room_type")}</span><strong>{selectedHotelBooking.requested_room_type || selectedHotelBooking.subtitle || memberText("portalPresentation.member_accommodation")}</strong></div>
        <div><UsersRound size={16} /><span>{memberText("portalPresentation.member_guests")}</span><strong>{selectedHotelBooking.guests_count || selectedHotelBooking.guestsCount || '—'}</strong></div>
        <div><ReceiptText size={16} /><span>{memberText("portalPresentation.member_full_stay_cost")}</span><strong>{selectedCost}</strong></div>
      </div>
      <section className={`mp-policy-card${selectedHotelBooking.can_cancel ? ' mp-policy-card--success' : ''}`}>
        <div className="mp-detail-section-card__title"><ShieldCheck size={16} /> {memberText("portalPresentation.member_cancellation_policy_status")}</div>
        {selectedHotelBooking.cancellation_policy?.member_policy_text ? <p>{selectedHotelBooking.cancellation_policy.member_policy_text}</p> : null}
        {selectedHotelBooking.can_cancel ? <p>{memberText("portalPresentation.member_you_may_cancel_this_hotel_rooms_booking_through_member_portal_cancellation")}</p> : <p>{hotelCancellationMessage(selectedHotelBooking)}</p>}
        {selectedHotelBooking.cancellation_policy?.cancellation_deadline_at || selectedHotelBooking.cancellation_deadline_at ? <div className="mp-row-sub">{memberText("portalPresentation.member_deadline")} {formatDateTime(selectedHotelBooking.cancellation_policy?.cancellation_deadline_at || selectedHotelBooking.cancellation_deadline_at)}</div> : null}
        {racquetRefundOutcomeLabel(selectedHotelBooking) ? <div className="mp-row-sub">{racquetRefundOutcomeLabel(selectedHotelBooking)}</div> : null}
        {hotelCancelError ? <div className="mp-empty mp-empty--error" role="alert">{memberText(hotelCancelError)}</div> : null}
      </section>
    </div>
    <div className="mp-reservation-detail__footer mp-reservation-detail__footer--actions-only">
      {selectedHotelBooking.can_cancel ? <button type="button" className="mp-primary mp-primary--danger" onClick={() => setHotelCancelConfirmOpen(true)}>{memberText("portalPresentation.member_cancel_booking")}</button> : null}
    </div>
  </>
  )


  const renderGenericDetailPanel = () => (
  <>
    <div className="mp-reservation-detail__scroll">
      <div className="mp-detail-hero">
        <div className="mp-detail-hero__icon"><Ticket size={22} /></div>
        <div>
          <div className="mp-reservation-card__eyebrow">{formatBookingType(selectedGenericBooking)}</div>
          <h3>{getBookingTitle(selectedGenericBooking) || memberText("portalPresentation.member_reservation")}</h3>
          <p>{getBookingVenue(selectedGenericBooking)}</p>
        </div>
        <span className="mp-status-pill mp-status-pill--strong">{formatFacilityBookingStatus(selectedGenericBooking?.status, memberText)}</span>
      </div>
      <div className="mp-detail-grid mp-detail-grid--premium">
        <div><CalendarClock size={16} /><span>{memberText("portalPresentation.member_date")}</span><strong>{formatDateOnly(selectedStart)}</strong></div>
        <div><Clock3 size={16} /><span>{memberText("facilityBooking.time")}</span><strong>{formatTimeOnly(selectedStart)}</strong></div>
        <div><MapPin size={16} /><span>{isDiningUpcomingBooking(selectedGenericBooking) ? memberText("portalPresentation.member_dining_area") : memberText("portalPresentation.member_venue_service")}</span><strong>{getBookingVenue(selectedGenericBooking)}</strong></div>
        {isDiningUpcomingBooking(selectedGenericBooking) ? <div><UsersRound size={16} /><span>{memberText("portalPresentation.member_party_size")}</span><strong>{selectedGenericBooking?.detailLine || selectedGenericBooking?.detail_line || '—'}</strong></div> : <div><ReceiptText size={16} /><span>{memberText("portalPresentation.member_cost")}</span><strong>{selectedCost || '—'}</strong></div>}
        {getMemberFacingBookingReference(selectedGenericBooking) ? (
          <div><Ticket size={16} /><span>{memberText("facilityBooking.reference")}</span><strong>{getMemberFacingBookingReference(selectedGenericBooking)}</strong></div>
        ) : null}
        <div><CheckCircle2 size={16} /><span>{memberText("portalPresentation.member_status")}</span><strong>{formatFacilityBookingStatus(selectedGenericBooking?.status, memberText)}</strong></div>
      </div>
      <section className={`mp-policy-card${selectedGenericBooking?.can_cancel ? ' mp-policy-card--success' : ''}`}>
        <div className="mp-detail-section-card__title"><ShieldCheck size={16} /> {memberText("portalPresentation.member_cancellation_policy_status")}</div>
        {selectedGenericBooking?.cancellation_policy?.member_policy_text ? <p>{selectedGenericBooking.cancellation_policy.member_policy_text}</p> : null}
        <p>{selectedGenericBooking?.can_cancel ? memberText("portalPresentation.member_you_may_cancel_this_dining_reservation_through_member_portal_cancellation") : cancellationUnavailableMessage(selectedGenericBooking?.cancellation_disabled_reason, memberText)}</p>
        {selectedGenericBooking?.cancellation_deadline_at ? <div className="mp-row-sub">{memberText("portalPresentation.member_deadline")} {formatDateTime(selectedGenericBooking.cancellation_deadline_at)}</div> : null}
      </section>
    </div>
    <div className="mp-reservation-detail__footer"><div className="mp-row-sub">{memberText("portalPresentation.member_need_to_adjust_this_booking_please_contact_the_club")}</div></div>
  </>
  )

  return (
    <div className={`mp-dashboard${isMobileApp ? ' mp-dashboard--native' : ''}`}>
      {isCompactDashboard ? <MobileMemberDashboard greeting={greeting} member={member} heroImageUrl={heroImageUrl} heroFocus={heroFocus} clubLogoUrl={clubLogoUrl} clubCode={clubCode}
        balance={outstandingBalance} currencyCode={currencyCode} summary={summary} bookings={safeBookings} upcomingLoading={upcomingLoading} upcomingError={upcomingError}
        onRetryBookings={onUpcomingBookingsRefresh} news={clubNews} newsLoading={clubNewsLoading} newsError={clubNewsError} newsConfirmed={clubNewsConfirmed}
        onRetryNews={() => setClubNewsRetry(value => value + 1)} onBook={quickActions[0].action} onPlay={() => navigate(`/${clubCode}/lets-play`)}
        onAccount={quickActions[3].action} onEvents={quickActions[1].action} onAcademy={() => navigate(`/${clubCode}/academy`)} onFrontDesk={quickActions[2].action}
        onAllBookings={openAllReservationsModal} onAllNews={() => setIsAllClubNewsOpen(true)} renderBooking={renderBookingRow} renderNews={renderClubNewsRow} /> : null}
      {!isCompactDashboard ? <div className="mp-dashboard__desktop">
      <section
        className="mp-hero-banner"
        style={{
          backgroundImage: heroImageUrl ? `url(${heroImageUrl})` : undefined,
          backgroundPosition: `${heroFocus.x}% ${heroFocus.y}%`,
        }}
        aria-label={memberText("portalPresentation.member_club_overview")}
      >
        <div className="mp-hero__overlay" />
        <div className="mp-hero__content">
          <div className="mp-hero__intro">
            <div className="mp-hero__brand">
              {clubLogoUrl ? (
                <img src={clubLogoUrl} alt={`${clubCode} logo`} />
              ) : (
                <span className="mp-hero__brand-text">PRIVANA</span>
              )}
            </div>
            <div>
              <div className="mp-hero__greeting">{greeting}</div>
              <h1 className="mp-hero__title mp-dashboard-name">
                {isMemberResolving ? (
                  <span
                    className="mp-hero__title-placeholder"
                    aria-label={memberText("portalPresentation.member_resolving_member_profile")}
                    role="status"
                  />
                ) : (
                  memberName
                )}
              </h1>
            </div>
          </div>
          <div className="mp-hero__stats mp-hero__stats--two-up" data-testid="member-dashboard-hero-stats">
            {stats.map((stat) => (
                <div key={stat.label} className="mp-stat-card">
                  <div className="mp-stat-value">
                    {stat.displayValue ??
                      (Number.isFinite(stat.value) ? Number(stat.value).toLocaleString() : '0')}
                  </div>
                  <div className="mp-stat-label">{stat.label}</div>
                </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mp-section">
        <div className="mp-card-grid">
          {quickActions.map((action) => {
            const Icon = action.icon
            return (
              <button key={action.label} type="button" className="mp-action-card" onClick={action.action} aria-label={action.label}>
                <div className="mp-action-icon">
                  <Icon size={20} />
                </div>
                <div className="mp-action-label">
                  <span className="mp-action-label__full" aria-hidden="true">{action.label}</span>
                  <span className="mp-action-label__mobile" aria-hidden="true">{action.mobileLabel}</span>
                </div>
              </button>
            )
          })}
          {!isMobileApp ? <button type="button" className="mp-action-card mp-academy-action" onClick={() => navigate(`/${clubCode}/academy`)}><div className="mp-action-icon"><GraduationCap size={20} aria-hidden="true" /></div><div><div className="mp-action-label">{academyTranslate('academyP2.title')}</div><small>{academyTranslate('academyP2.subtitle')}</small></div></button> : null}
        </div>
        {isMobileApp ? <button type="button" className="mp-lets-play-entry" onClick={() => navigate(`/${clubCode}/lets-play`)}><Goal size={22} aria-hidden="true" /><span>{memberText("portalPresentation.member_let_s_play")}</span><ChevronRight size={20} aria-hidden="true" /></button> : null}
        {isMobileApp ? <button type="button" className="mp-lets-play-entry mp-academy-action" onClick={() => navigate(`/${clubCode}/academy`)}><GraduationCap size={22} aria-hidden="true" /><span>{academyTranslate('academyP2.title')}<small>{academyTranslate('academyP2.subtitle')}</small></span><ChevronRight size={20} aria-hidden="true" /></button> : null}
      </section>

      <section className="mp-section mp-dual-grid mp-dashboard-cards">
        <div className="mp-panel-card">
          <div className="mp-panel-header">
            <div>
              <div className="mp-panel-title">{memberText("portalPresentation.member_upcoming_bookings")}</div>
              <div className="mp-panel-subtitle">{memberText("portalPresentation.member_your_next_experiences_at_the_club")}</div>
            </div>
            <button
              ref={viewAllButtonRef}
              type="button"
              className="mp-link"
              onClick={openAllReservationsModal}
            >
              <span>{memberText("portalPresentation.member_view_all")}</span>
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
          <div className="mp-panel-body">
            {upcomingLoading ? (
              <div className="mp-empty">{memberText("membersHome.bookingsLoading")}</div>
            ) : safeBookings.length === 0 ? (
              <div className="mp-empty">{memberText("membersHome.noBookings")}</div>
            ) : (
              previewBookings.map((booking) => renderBookingRow(booking))
            )}
          </div>
        </div>

        {member?.portal_access_enabled === false ? null : (
          <div className="mp-panel-card">
            <div className="mp-panel-header">
              <div>
                <div className="mp-panel-title">{memberText("portalPresentation.member_club_news")}</div>
                <div className="mp-panel-subtitle">{memberText("portalPresentation.member_check_back_here_soon_for_more_exciting_club_news")}</div>
              </div>
              <button type="button" className="mp-link" onClick={() => setIsAllClubNewsOpen(true)}><span>{memberText("portalPresentation.member_view_all")}</span><ChevronRight className="mp-desktop-news-chevron" size={14} aria-hidden="true" /></button>
            </div>
            <div className="mp-panel-body">
              {clubNewsLoading && clubNews.length === 0 ? (
                <div className="mp-resource-skeleton" aria-label={memberText("portalPresentation.member_loading_club_news")} />
              ) : clubNewsError && clubNews.length === 0 ? (
                <div className="mp-empty">{memberText(clubNewsError)} <button className="mp-link" type="button" onClick={() => setClubNewsRetry(value => value + 1)}>{memberText("portalPresentation.member_retry")}</button></div>
              ) : clubNewsConfirmed && clubNews.length === 0 ? (
                <div className="mp-empty"><span className="mp-news-empty-legacy">{memberText("portalPresentation.member_check_back_here_soon_for_more_exciting_club_news")}</span><span className="mp-news-empty-desktop">{memberText("membersHome.noNews")}</span></div>
              ) : (
                previewClubNews.map((item) => renderClubNewsRow(item))
              )}
            </div>
          </div>
        )}
      </section>



      </div> : null}

      {isAllClubNewsOpen ? (
        <MemberDashboardModal
          labelledBy="all-club-news-title"
          onClose={() => setIsAllClubNewsOpen(false)}
          closeLabel={memberText("portalPresentation.member_close_all_club_news")}
          testId="all-club-news-modal"
          size="dashboard"
        >
          <header className="mp-all-reservations__header">
            <div>
              <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_member_updates")}</div>
              <div className="mp-panel-title" id="all-club-news-title">{memberText("portalPresentation.member_club_news")}</div>
            </div>
          </header>
          <div className="mp-dashboard-modal__scroll">
            {clubNewsLoading && clubNews.length === 0 ? <div className="mp-resource-skeleton" aria-label={memberText("portalPresentation.member_loading_club_news")} /> : clubNews.length ? (
              clubNews.map((item) => renderClubNewsRow(item))
            ) : clubNewsError ? <div className="mp-empty">{memberText(clubNewsError)} <button className="mp-link" type="button" onClick={() => setClubNewsRetry(value => value + 1)}>{memberText("portalPresentation.member_retry")}</button></div> : clubNewsConfirmed ? <div className="mp-empty">{memberText("portalPresentation.member_check_back_here_soon_for_more_exciting_club_news")}</div> : null}
          </div>
        </MemberDashboardModal>
      ) : null}

      {activeEntryNotification ? (
        <MemberDashboardModal
          labelledBy="entry-notification-title"
          onClose={() => {}}
          closeLabel={memberText("portalPresentation.member_close_entry_notification")}
          testId="entry-notification-modal"
          size="compact"
          closeOnBackdrop={false}
          closeOnEscape={false}
          showCloseButton={false}
        >
          <header className="mp-dashboard-modal__header">
            <div>
              <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_member_portal_entry_notification")}</div>
              <h2 className="mp-panel-title" id="entry-notification-title">{activeEntryNotification.title}</h2>
            </div>
          </header>
          <div className="mp-dashboard-modal__scroll">
            {activeEntryNotification.announcement_type === 'upload' ? (
              entryNotificationPdfUrl ? <iframe className="mp-dashboard-modal__document" title={activeEntryNotification.title} src={entryNotificationPdfUrl} /> : <div className="mp-empty">{memberText("portalPresentation.member_preparing_pdf_viewer")}</div>
            ) : (
              <div className="mp-reservation-detail__notes" style={{ whiteSpace: 'pre-wrap' }}>{activeEntryNotification.content}</div>
            )}
          </div>
          <footer className="mp-dashboard-modal__footer">
            <button type="button" className="mp-primary-button mp-entry-notification__mark-read" onClick={markEntryNotificationRead} disabled={entryNotificationReadLoading} aria-busy={entryNotificationReadLoading}>{memberText("portalPresentation.member_mark_as_read")}</button>
          </footer>
        </MemberDashboardModal>
      ) : null}

      {selectedClubNews ? (
        <MemberDashboardModal
          labelledBy="club-news-title"
          onClose={() => setSelectedClubNews(null)}
          closeLabel={memberText("portalPresentation.member_close_club_news")}
          testId="club-news-modal"
          size="dashboard"
        >
          <header className="mp-all-reservations__header">
            <div>
              <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_club_news")}</div>
              <h2 className="mp-panel-title" id="club-news-title">{selectedClubNews.title}</h2>
              <p className="mp-row-sub">{selectedClubNews.published_at ? new Date(selectedClubNews.published_at).toLocaleDateString() : ''}</p>
            </div>
          </header>
          <div className="mp-dashboard-modal__scroll">
            {selectedClubNews.announcement_type === 'upload' ? (
              clubNewsPdfUrl ? <iframe className="mp-dashboard-modal__document" title={selectedClubNews.title} src={clubNewsPdfUrl} /> : <div className="mp-empty">{memberText("portalPresentation.member_preparing_pdf_viewer")}</div>
            ) : (
              <div className="mp-reservation-detail__notes" style={{ whiteSpace: 'pre-wrap' }}>{selectedClubNews.content}</div>
            )}
          </div>
        </MemberDashboardModal>
      ) : null}

      {isContactFrontDeskOpen ? (
        <MemberFrontDeskModal clubCode={clubCode} onClose={() => setIsContactFrontDeskOpen(false)} />
      ) : null}

      {isAllReservationsOpen ? (
        <MemberDashboardModal
          labelledBy="all-upcoming-reservations-title"
          onClose={closeAllReservationsModal}
          closeLabel={memberText("portalPresentation.member_close_all_upcoming_bookings")}
          testId="all-upcoming-reservations-modal"
          screenKey={`${selectedUpcomingBooking?.type || selectedUpcomingBooking?.module || ""}:${selectedUpcomingBooking?.id || "list"}`}
          bodyClassName="mp-all-upcoming-reservations-modal"
          size="dashboard"
        >
            <header className="mp-all-reservations__header">
              <div>
                <div className="mp-reservation-card__eyebrow">{memberText("portalPresentation.member_member_bookings")}</div>
                <div className="mp-panel-title" id="all-upcoming-reservations-title"> {memberText("portalPresentation.member_all_upcoming_experiences")} </div>
              </div>
              <div className="mp-all-reservations__count">{safeBookings.length} {memberText("portalPresentation.member_upcoming")}</div>
            </header>
            <div className="mp-all-reservations__layout">
              <aside className="mp-all-reservations__list-section" aria-label={memberText("portalPresentation.member_upcoming_bookings")}>
                <div className="mp-all-reservations__list-heading"><div className="mp-all-reservations__list-title">{memberText("portalPresentation.member_upcoming_bookings")}</div>{safeBookings.length > 1 ? <span>{Math.min(upcomingCarouselIndex + 1, safeBookings.length)} {memberText("portalPresentation.member_of")} {safeBookings.length}</span> : null}</div>
                <div ref={upcomingCarouselRef} className="mp-all-reservations__list" role="region" aria-roledescription="carousel" aria-label={memberText("portalPresentation.member_upcoming_bookings_carousel")} onScroll={(event) => {
                  const cards = [...event.currentTarget.querySelectorAll('[data-carousel-index]')]
                  if (!cards.length) return
                  const left = event.currentTarget.scrollLeft
                  const nearest = cards.reduce((best, card) => Math.abs(card.offsetLeft - cards[0].offsetLeft - left) < Math.abs(best.offsetLeft - cards[0].offsetLeft - left) ? card : best, cards[0])
                  const nextIndex = Math.max(0, Math.min(cards.length - 1, Number(nearest.dataset.carouselIndex) || 0))
                  setUpcomingCarouselIndex(nextIndex)
                  const nextBooking = safeBookings[nextIndex]
                  const nextBookingId = upcomingDetailIdentity(nextBooking)
                  if (nextBooking && nextBookingId && nextBookingId !== upcomingCarouselBookingIdRef.current) {
                    upcomingCarouselBookingIdRef.current = nextBookingId
                    void openUpcomingBooking(nextBooking)
                  }
                }}>
                {upcomingLoading ? (
                  <div className="mp-empty">{memberText("membersHome.bookingsLoading")}</div>
                ) : safeBookings.length === 0 ? (
                  <div className="mp-empty">{memberText("membersHome.noBookings")}</div>
                ) : (
                  safeBookings.map((booking, index) => renderBookingRow(booking, { detailed: true, carouselIndex: index }))
                )}
                </div>
              </aside>
              <section className="mp-reservation-detail" aria-live="polite">
                {selectedBookingLoading ? <div role="status">{academyTranslate('portalPresentation.member_loading_reservation_details')}</div> : null}
                {upcomingDetailError ? <div role="alert">{academyTranslate(`bookingDetails.${upcomingDetailError}`)}{upcomingDetailError === 'failed' && selectedUpcomingBooking ? <button type="button" className="mp-link" onClick={() => openUpcomingBooking(selectedUpcomingBooking, { prefetch: false })}>{academyTranslate('portalPresentation.member_retry')}</button> : null}</div> : null}
                {!selectedUpcomingBooking && !selectedGolfBooking ? (
                  <div className="mp-reservation-detail__empty">
                    <div className="mp-detail-hero__icon"><CalendarClock size={24} /></div>
                    <h3>{memberText("portalPresentation.member_select_a_booking_to_view_details")}</h3>
                    <p>{memberText("portalPresentation.member_choose_a_booking_from_the_list_to_see_policy_information_players_add_ons_payment_details_and_available_actions")}</p>
                  </div>
                ) : selectedGolfBooking ? (
                  renderGolfDetailPanel()
                ) : selectedRacquetBooking ? (
                  renderRacquetDetailPanel()
                ) : selectedSpaBooking ? (
                  renderSpaDetailPanel()
                ) : selectedFitnessBooking ? (
                  renderFitnessDetailPanel()
                ) : selectedHotelBooking ? (
                  renderHotelDetailPanel()
                ) : selectedGenericBooking ? (
                  renderGenericDetailPanel()
                ) : null}
              </section>
            </div>
        </MemberDashboardModal>
      ) : null}



      <MemberPortalToast message={golfCancelSuccess ? memberText(golfCancelSuccess) : ''} resetKey={golfCancelSuccessKey} onDismiss={() => setGolfCancelSuccess('')} />
      <MemberPortalToast message={racquetCancelSuccess ? memberText(racquetCancelSuccess) : ''} resetKey={racquetCancelSuccessKey} onDismiss={() => setRacquetCancelSuccess('')} />
      <MemberPortalToast message={spaCancelSuccess ? memberText(spaCancelSuccess) : ''} resetKey={spaCancelSuccessKey} onDismiss={() => setSpaCancelSuccess('')} />
      <MemberPortalToast message={fitnessCancelSuccess ? memberText(fitnessCancelSuccess) : ''} resetKey={fitnessCancelSuccessKey} onDismiss={() => setFitnessCancelSuccess('')} />
      <MemberPortalToast message={hotelCancelSuccess ? memberText(hotelCancelSuccess) : ''} resetKey={hotelCancelSuccessKey} onDismiss={() => setHotelCancelSuccess('')} />
      <MemberPortalToast message={golfCancelErrorToast ? memberText(golfCancelErrorToast) : ''} variant="error" resetKey={golfCancelErrorToastKey} onDismiss={() => setGolfCancelErrorToast('')} />
      <MemberPortalToast message={racquetCancelErrorToast ? memberText(racquetCancelErrorToast) : ''} variant="error" resetKey={racquetCancelErrorToastKey} onDismiss={() => setRacquetCancelErrorToast('')} />
      <MemberPortalToast message={spaCancelErrorToast ? memberText(spaCancelErrorToast) : ''} variant="error" resetKey={spaCancelErrorToastKey} onDismiss={() => setSpaCancelErrorToast('')} />
      <MemberPortalToast message={fitnessCancelErrorToast ? memberText(fitnessCancelErrorToast) : ''} variant="error" resetKey={fitnessCancelErrorToastKey} onDismiss={() => setFitnessCancelErrorToast('')} />
      <MemberPortalToast message={hotelCancelErrorToast ? memberText(hotelCancelErrorToast) : ''} variant="error" resetKey={hotelCancelErrorToastKey} onDismiss={() => setHotelCancelErrorToast('')} />

      {golfCancelConfirmOpen ? (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setGolfCancelConfirmOpen(false)} />
          <div className="mp-modal__card" role="dialog" aria-modal="true" aria-labelledby="cancel-golf-title">
            <button type="button" className="mp-modal__close" onClick={() => setGolfCancelConfirmOpen(false)} aria-label={memberText("portalPresentation.member_close_cancellation_confirmation")}>×</button>
            <div className="mp-panel-title" id="cancel-golf-title">{memberText("portalPresentation.member_cancel_this_tee_time")}</div>
            <div className="mp-text-muted mp-panel-subtitle">{memberText("portalPresentation.member_this_will_cancel_your_tee_time_and_update_your_member_account_this_cannot_be_undone_online")}</div>
            {golfCancelError ? <div className="mp-empty" role="alert">{memberText(golfCancelError)}</div> : null}
            <div className="mp-panel-body">
              <button type="button" className="mp-link" onClick={() => setGolfCancelConfirmOpen(false)}>{memberText("portalPresentation.member_keep_booking")}</button>
              <button type="button" className="mp-primary mp-primary--danger" onClick={cancelGolfBooking}>{memberText("portalPresentation.member_cancel_tee_time")}</button>
            </div>
          </div>
        </div>
      ) : null}



      {racquetCancelConfirmOpen ? (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setRacquetCancelConfirmOpen(false)} />
          <div className="mp-modal__card" role="dialog" aria-modal="true" aria-labelledby="cancel-racquet-title">
            <button type="button" className="mp-modal__close" onClick={() => setRacquetCancelConfirmOpen(false)} aria-label={memberText("portalPresentation.member_close_cancellation_confirmation")}>×</button>
            <div className="mp-panel-title" id="cancel-racquet-title">{memberText("portalPresentation.member_cancel_this_racquet_booking")}</div>
            <div className="mp-text-muted mp-panel-subtitle">{memberText("portalPresentation.member_this_will_cancel_your_booking_and_apply_the_club_cancellation_policy")}</div>
            {racquetCancelError ? <div className="mp-empty" role="alert">{memberText(racquetCancelError)}</div> : null}
            <div className="mp-panel-body">
              <button type="button" className="mp-link" onClick={() => setRacquetCancelConfirmOpen(false)}>{memberText("portalPresentation.member_keep_booking")}</button>
              <button type="button" className="mp-primary mp-primary--danger" onClick={cancelRacquetBooking}>{memberText("portalPresentation.member_cancel_booking")}</button>
            </div>
          </div>
        </div>
      ) : null}

      {spaCancelConfirmOpen ? (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setSpaCancelConfirmOpen(false)} />
          <div className="mp-modal__card" role="dialog" aria-modal="true" aria-labelledby="cancel-spa-title">
            <button type="button" className="mp-modal__close" onClick={() => setSpaCancelConfirmOpen(false)} aria-label={memberText("portalPresentation.member_close_cancellation_confirmation")}>×</button>
            <div className="mp-panel-title" id="cancel-spa-title">{memberText("portalPresentation.member_cancel_this_spa_booking")}</div>
            <div className="mp-text-muted mp-panel-subtitle">{memberText("portalPresentation.member_this_will_cancel_your_booking_and_apply_the_club_cancellation_policy")}</div>
            {spaCancelError ? <div className="mp-empty" role="alert">{memberText(spaCancelError)}</div> : null}
            <div className="mp-panel-body">
              <button type="button" className="mp-link" onClick={() => setSpaCancelConfirmOpen(false)}>{memberText("portalPresentation.member_keep_booking")}</button>
              <button type="button" className="mp-primary mp-primary--danger" onClick={cancelSpaBooking}>{memberText("portalPresentation.member_cancel_booking")}</button>
            </div>
          </div>
        </div>
      ) : null}

      {fitnessCancelConfirmOpen ? (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setFitnessCancelConfirmOpen(false)} />
          <div className="mp-modal__card" role="dialog" aria-modal="true" aria-labelledby="cancel-fitness-title">
            <button type="button" className="mp-modal__close" onClick={() => setFitnessCancelConfirmOpen(false)} aria-label={memberText("portalPresentation.member_close_cancellation_confirmation")}>×</button>
            <div className="mp-panel-title" id="cancel-fitness-title">{memberText("portalPresentation.member_cancel_this_fitness_booking")}</div>
            <div className="mp-text-muted mp-panel-subtitle">{memberText("portalPresentation.member_this_will_cancel_your_booking_and_apply_the_club_cancellation_policy")}</div>
            {fitnessCancelError ? <div className="mp-empty" role="alert">{memberText(fitnessCancelError)}</div> : null}
            <div className="mp-panel-body">
              <button type="button" className="mp-link" onClick={() => setFitnessCancelConfirmOpen(false)}>{memberText("portalPresentation.member_keep_booking")}</button>
              <button type="button" className="mp-primary mp-primary--danger" onClick={cancelFitnessBooking}>{memberText("portalPresentation.member_cancel_booking")}</button>
            </div>
          </div>
        </div>
      ) : null}

      {hotelCancelConfirmOpen ? (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setHotelCancelConfirmOpen(false)} />
          <div className="mp-modal__card" role="dialog" aria-modal="true" aria-labelledby="cancel-hotel-title">
            <button type="button" className="mp-modal__close" onClick={() => setHotelCancelConfirmOpen(false)} aria-label={memberText("portalPresentation.member_close_cancellation_confirmation")}>×</button>
            <div className="mp-panel-title" id="cancel-hotel-title">{memberText("portalPresentation.member_cancel_this_hotel_rooms_booking")}</div>
            <div className="mp-text-muted mp-panel-subtitle">{memberText("portalPresentation.member_this_will_cancel_your_stay_and_apply_the_club_cancellation_policy")}</div>
            {hotelCancelError ? <div className="mp-empty" role="alert">{memberText(hotelCancelError)}</div> : null}
            <div className="mp-panel-body">
              <button type="button" className="mp-link" onClick={() => setHotelCancelConfirmOpen(false)}>{memberText("portalPresentation.member_keep_booking")}</button>
              <button type="button" className="mp-primary mp-primary--danger" onClick={cancelHotelBooking}>{memberText("portalPresentation.member_cancel_booking")}</button>
            </div>
          </div>
        </div>
      ) : null}


    </div>
  )
}
